import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";
import CropOutlinedIcon from "@mui/icons-material/CropOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import CheckBoxOutlineBlankOutlinedIcon from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import SvgIcon from "@mui/material/SvgIcon";
import GestureOutlinedIcon from "@mui/icons-material/GestureOutlined";
import HorizontalRuleOutlinedIcon from "@mui/icons-material/HorizontalRuleOutlined";
import CallMadeOutlinedIcon from "@mui/icons-material/CallMadeOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import AutoFixOffOutlinedIcon from "@mui/icons-material/AutoFixOffOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import {
  addSkillAgentMoodboardAssets,
  autoSkillAgentMoodboardPages,
  contourStrokeSkillAgentMoodboardAsset,
  cropSkillAgentMoodboardAsset,
  deleteSkillAgentMoodboard,
  deleteSkillAgentMoodboardAsset,
  exportSkillAgentMoodboardPdf,
  fetchSkillAgentMoodboardAssetBlobUrl,
  getSkillAgentMoodboardById,
  getSkillAgentMoodboardPool,
  removeBgSkillAgentMoodboardAsset,
  saveSkillAgentMoodboardPages,
  updateSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import {
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  listSkillAgentSearches,
} from "../../api/Skills/skillAgentSearchesApi";
import { pageFieldSx } from "../../styles/pageModernStyles";
import { socket } from "../../socket";
import { moodboardStatusLabel } from "./skillAgentTaskTree";
import {
  applyAviatorPresetToPage,
  clampSlot,
  clampMark,
  coverFit,
  emptyFreePage,
  MIN_SLOT,
  newSlotId,
  newMarkId,
  normalizeChromeStyle,
  normalizeFreePage,
  normalizePages,
  snapSlot,
  sortSlotsByLayer,
  splitCategoryLabel,
  splitStudioLine,
  CHROME_SHAPES,
  chromeBarClipPath,
  DEFAULT_MARK_COLOR,
  markFontCss,
  MARK_FONT_FAMILIES,
  normalizeMarkFontFamily,
  translateMark,
} from "./skillAgentMoodboardLayout";

const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

/** Zoom de vista de la página (no afecta al PDF). */
const PAGE_VIEW_ZOOM_MIN = 0.4;
const PAGE_VIEW_ZOOM_MAX = 1.25;
const PAGE_VIEW_ZOOM_STEP = 0.1;

function clampPageViewZoom(z) {
  const n = Number(z);
  if (!Number.isFinite(n)) return 1;
  return (
    Math.round(
      Math.min(PAGE_VIEW_ZOOM_MAX, Math.max(PAGE_VIEW_ZOOM_MIN, n)) * 100,
    ) / 100
  );
}

/** Cuadrado punteado + círculo sólido → pasar a circular (estilo Illustrator). */
function ShapeToCircleIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" fontSize="small" {...props}>
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="1.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 1.8"
      />
      <circle
        cx="12"
        cy="12"
        r="5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.85"
      />
    </SvgIcon>
  );
}

/** Cuadrado sólido + círculo punteado → pasar a cuadrada. */
function ShapeToSquareIcon(props) {
  return (
    <SvgIcon viewBox="0 0 24 24" fontSize="small" {...props}>
      <rect
        x="3.25"
        y="3.25"
        width="17.5"
        height="17.5"
        rx="1.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.85"
      />
      <circle
        cx="12"
        cy="12"
        r="5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.4 1.8"
      />
    </SvgIcon>
  );
}

/** Grupo de toolbar con label de texto. */
function ToolGroup({ label, accent = false, grow = false, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.15,
        px: 0.5,
        py: 0.15,
        borderRadius: 1.5,
        bgcolor: (t) =>
          accent
            ? alpha(t.palette.primary.main, 0.04)
            : alpha(t.palette.text.primary, 0.03),
        border: "1px solid",
        borderColor: (t) =>
          accent ? alpha(t.palette.primary.main, 0.18) : "divider",
        flex: grow ? 1 : "0 0 auto",
        minWidth: grow ? 0 : undefined,
        maxWidth: grow ? "100%" : undefined,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          px: 0.6,
          fontWeight: 800,
          fontSize: "0.62rem",
          letterSpacing: 0.04,
          color: accent ? "primary.main" : "text.secondary",
          textTransform: "uppercase",
          userSelect: "none",
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** Selector visual de forma para cabecera/pie. */
function ChromeShapePicker({ value, which, onChange }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {t("skillAgent.moodboard.formShape")}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {CHROME_SHAPES.map((opt) => {
          const selected = value === opt.id;
          const clip = chromeBarClipPath(opt.id, which, "10px");
          const shapeLabel = t(
            `skillAgent.moodboard.chromeShape.${opt.id}`,
            opt.label,
          );
          return (
            <Tooltip key={opt.id} title={shapeLabel}>
              <Box
                component="button"
                type="button"
                onClick={() => onChange(opt.id)}
                aria-label={shapeLabel}
                aria-pressed={selected}
                sx={{
                  width: 72,
                  p: 0.6,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  bgcolor: (t) =>
                    selected
                      ? alpha(t.palette.primary.main, 0.08)
                      : alpha(t.palette.background.paper, 0.8),
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  transition: "border-color 0.15s, background-color 0.15s",
                  "&:hover": {
                    borderColor: "primary.main",
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: 18,
                    bgcolor: selected ? "primary.main" : "text.secondary",
                    opacity: selected ? 1 : 0.55,
                    clipPath: clip === "none" ? undefined : clip,
                    borderRadius: clip === "none" ? 0.5 : 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: selected ? 800 : 600,
                    fontSize: "0.62rem",
                    color: selected ? "primary.main" : "text.secondary",
                    lineHeight: 1,
                  }}
                >
                  {shapeLabel}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
}

function markStrokeSvg(stroke) {
  return Math.max(0.0015, (Number(stroke) || 2) * 0.0022);
}

function pointsToSvgPath(points) {
  if (!points?.length) return "";
  return points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x} ${p.y}`).join(" ");
}

function arrowHeadPoints(x1, y1, x2, y2, size = 0.018) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  return [
    [x2, y2],
    [x2 - ux * size + px * size * 0.45, y2 - uy * size + py * size * 0.45],
    [x2 - ux * size - px * size * 0.45, y2 - uy * size - py * size * 0.45],
  ]
    .map(([x, y]) => `${x},${y}`)
    .join(" ");
}

function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = x1 + t * dx;
  const qy = y1 + t * dy;
  return Math.hypot(px - qx, py - qy);
}

function markHitTest(mark, x, y, tol = 0.02, { fill = false } = {}) {
  if (!mark) return false;
  if (mark.type === "path") {
    const pts = mark.points || [];
    for (let i = 1; i < pts.length; i += 1) {
      if (
        distToSegment(x, y, pts[i - 1].x, pts[i - 1].y, pts[i].x, pts[i].y) <=
        tol
      ) {
        return true;
      }
    }
    return pts.some((p) => Math.hypot(p.x - x, p.y - y) <= tol);
  }
  if (mark.type === "line" || mark.type === "arrow") {
    return distToSegment(x, y, mark.x1, mark.y1, mark.x2, mark.y2) <= tol;
  }
  if (mark.type === "rect") {
    const inside =
      x >= mark.x - tol &&
      x <= mark.x + mark.w + tol &&
      y >= mark.y - tol &&
      y <= mark.y + mark.h + tol;
    if (fill && inside) return true;
    const nearEdge =
      Math.abs(x - mark.x) <= tol ||
      Math.abs(x - (mark.x + mark.w)) <= tol ||
      Math.abs(y - mark.y) <= tol ||
      Math.abs(y - (mark.y + mark.h)) <= tol;
    return inside && nearEdge
      ? true
      : Math.abs(x - mark.x) <= tol ||
          Math.abs(x - (mark.x + mark.w)) <= tol ||
          Math.abs(y - mark.y) <= tol ||
          Math.abs(y - (mark.y + mark.h)) <= tol
        ? true
        : x >= mark.x &&
          x <= mark.x + mark.w &&
          y >= mark.y &&
          y <= mark.y + mark.h &&
          Math.min(
            x - mark.x,
            mark.x + mark.w - x,
            y - mark.y,
            mark.y + mark.h - y,
          ) <=
            tol * 1.5;
  }
  if (mark.type === "ellipse") {
    const cx = mark.x + mark.w / 2;
    const cy = mark.y + mark.h / 2;
    const rx = Math.max(mark.w / 2, 0.001);
    const ry = Math.max(mark.h / 2, 0.001);
    const nx = (x - cx) / rx;
    const ny = (y - cy) / ry;
    const d = Math.hypot(nx, ny);
    if (fill && d <= 1 + tol / Math.min(rx, ry)) return true;
    return (
      Math.abs(d - 1) <= tol / Math.min(rx, ry) ||
      (d <= 1 && d >= 1 - tol / Math.min(rx, ry))
    );
  }
  if (mark.type === "text") {
    const tw = Math.max(0.05, (String(mark.text || "").length || 1) * 0.014);
    const th = Math.max(0.03, (mark.fontSize || 14) * 0.0028);
    return (
      x >= mark.x - tol &&
      x <= mark.x + tw + tol &&
      y >= mark.y - th - tol &&
      y <= mark.y + tol
    );
  }
  return false;
}

function findMarkAt(marks, x, y, tol = 0.025) {
  const list = Array.isArray(marks) ? marks : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (markHitTest(list[i], x, y, tol, { fill: true })) return list[i];
  }
  return null;
}

function MarkSvgShape({
  mark,
  draft = false,
  selected = false,
  hit = false,
  onPointerDown,
}) {
  if (!mark) return null;
  const sw = markStrokeSvg(mark.stroke) * (selected ? 1.35 : 1);
  const opacity = draft ? 0.75 : 1;
  // En select: hit-test solo la marca; el vacío del SVG deja pasar a las casillas
  const pe = hit ? "all" : "none";
  const hitProps = {
    style: { pointerEvents: pe, cursor: hit ? "move" : undefined },
    onPointerDown: hit && onPointerDown ? onPointerDown : undefined,
  };
  const strokeProps = {
    fill: "none",
    stroke: mark.color || DEFAULT_MARK_COLOR,
    strokeWidth: sw,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    opacity,
    filter: selected ? "url(#markSelectedGlow)" : undefined,
    ...hitProps,
  };

  if (mark.type === "path") {
    return <path d={pointsToSvgPath(mark.points)} {...strokeProps} />;
  }
  if (mark.type === "line") {
    return (
      <line
        x1={mark.x1}
        y1={mark.y1}
        x2={mark.x2}
        y2={mark.y2}
        {...strokeProps}
      />
    );
  }
  if (mark.type === "arrow") {
    return (
      <g {...hitProps}>
        <line
          x1={mark.x1}
          y1={mark.y1}
          x2={mark.x2}
          y2={mark.y2}
          fill="none"
          stroke={mark.color || DEFAULT_MARK_COLOR}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={opacity}
          filter={selected ? "url(#markSelectedGlow)" : undefined}
          style={{
            pointerEvents: hit ? "stroke" : "none",
            cursor: hit ? "move" : undefined,
          }}
        />
        <polygon
          points={arrowHeadPoints(mark.x1, mark.y1, mark.x2, mark.y2)}
          fill={mark.color || DEFAULT_MARK_COLOR}
          stroke="none"
          opacity={opacity}
          style={{
            pointerEvents: hit ? "all" : "none",
            cursor: hit ? "move" : undefined,
          }}
        />
      </g>
    );
  }
  if (mark.type === "rect") {
    return (
      <rect
        x={mark.x}
        y={mark.y}
        width={Math.max(mark.w, 0.001)}
        height={Math.max(mark.h, 0.001)}
        {...strokeProps}
      />
    );
  }
  if (mark.type === "ellipse") {
    return (
      <ellipse
        cx={mark.x + mark.w / 2}
        cy={mark.y + mark.h / 2}
        rx={Math.max(mark.w / 2, 0.001)}
        ry={Math.max(mark.h / 2, 0.001)}
        {...strokeProps}
      />
    );
  }
  return null;
}

function MarksOverlay({
  marks = [],
  tool,
  color,
  stroke,
  draftMark,
  textDraft,
  markFontSize = 14,
  markFontFamily = "sans",
  selectedMarkId,
  onSelectMark,
  onMoveMark,
  onPointerNorm,
  onCommitMark,
  onEraseAt,
  onEraseAlong,
  onStartText,
  onEditText,
  onChangeTextDraft,
  onCommitText,
  onCancelText,
}) {
  const svgRef = useRef(null);
  const drawingRef = useRef(false);
  const lastEraseRef = useRef(null);
  const dragRef = useRef(null);

  const toNorm = useCallback((clientX, clientY) => {
    const el = svgRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return null;
    return {
      x: Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
    };
  }, []);

  const active = Boolean(tool);
  // Sin herramienta de dibujo (o flecha): vacío deja pasar a casillas; solo las marcas capturan
  const idle = !tool || tool === "select";
  const passThroughEmpty = idle;

  const captureTarget = () => svgRef.current;

  const beginDrag = (mark, p, e) => {
    dragRef.current = {
      id: mark.id,
      startX: p.x,
      startY: p.y,
      orig: mark,
      moved: false,
    };
    // Capture + listeners en window: en select el overlay tiene pointer-events none
    try {
      (e.currentTarget || captureTarget())?.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    const onMove = (ev) => handlePointerMove(ev);
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      handlePointerUp(ev);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const finishDrag = (e) => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (!drag.moved && tool === "text" && drag.orig?.type === "text") {
      onEditText?.(drag.orig);
    }
    dragRef.current = null;
    try {
      captureTarget()?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    return true;
  };

  const handlePointerDown = (e) => {
    if (!tool) return;
    e.preventDefault();
    e.stopPropagation();
    const p = toNorm(e.clientX, e.clientY);
    if (!p) return;
    if (tool === "select" || !tool) {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit) {
        beginDrag(hit, p, e);
        onSelectMark?.(hit.id);
      } else {
        onSelectMark?.(null);
      }
      return;
    }
    if (tool === "eraser") {
      drawingRef.current = true;
      lastEraseRef.current = p;
      onEraseAt?.(p.x, p.y);
      captureTarget()?.setPointerCapture?.(e.pointerId);
      return;
    }
    if (tool === "text") {
      const hit = findMarkAt(marks, p.x, p.y);
      if (hit?.type === "text") {
        beginDrag(hit, p, e);
        return;
      }
      onStartText?.(p.x, p.y);
      return;
    }
    drawingRef.current = true;
    captureTarget()?.setPointerCapture?.(e.pointerId);
    if (tool === "pen") {
      onPointerNorm?.({
        type: "path",
        color,
        stroke,
        points: [p],
      });
      return;
    }
    if (tool === "line" || tool === "arrow") {
      onPointerNorm?.({
        type: tool,
        color,
        stroke,
        x1: p.x,
        y1: p.y,
        x2: p.x,
        y2: p.y,
      });
      return;
    }
    if (tool === "ellipse" || tool === "rect") {
      onPointerNorm?.({
        type: tool,
        color,
        stroke,
        x: p.x,
        y: p.y,
        w: 0,
        h: 0,
        _ox: p.x,
        _oy: p.y,
        _shift: e.shiftKey,
      });
    }
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (drag) {
      const p = toNorm(e.clientX, e.clientY);
      if (!p) return;
      const dx = p.x - drag.startX;
      const dy = p.y - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) > 0.004) {
        dragRef.current = { ...drag, moved: true };
      }
      onMoveMark?.(drag.id, translateMark(drag.orig, dx, dy));
      return;
    }
    if (!tool || !drawingRef.current) return;
    const p = toNorm(e.clientX, e.clientY);
    if (!p) return;
    if (tool === "eraser") {
      onEraseAlong?.(lastEraseRef.current, p);
      lastEraseRef.current = p;
      return;
    }
    onPointerNorm?.((prev) => {
      if (!prev) return prev;
      if (prev.type === "path") {
        const pts = prev.points || [];
        const last = pts[pts.length - 1];
        if (last && Math.hypot(last.x - p.x, last.y - p.y) < 0.004) {
          return prev;
        }
        return { ...prev, points: [...pts, p] };
      }
      if (prev.type === "line" || prev.type === "arrow") {
        return { ...prev, x2: p.x, y2: p.y };
      }
      if (prev.type === "ellipse" || prev.type === "rect") {
        const ox = prev._ox ?? prev.x;
        const oy = prev._oy ?? prev.y;
        let w = p.x - ox;
        let h = p.y - oy;
        if (e.shiftKey || (prev.type === "ellipse" && e.shiftKey)) {
          const side = Math.max(Math.abs(w), Math.abs(h));
          w = Math.sign(w || 1) * side;
          h = Math.sign(h || 1) * side;
        }
        if (e.shiftKey) {
          const side = Math.max(Math.abs(w), Math.abs(h));
          w = Math.sign(p.x - ox || 1) * side;
          h = Math.sign(p.y - oy || 1) * side;
        }
        return {
          ...prev,
          x: w < 0 ? ox + w : ox,
          y: h < 0 ? oy + h : oy,
          w: Math.abs(w),
          h: Math.abs(h),
          _ox: ox,
          _oy: oy,
        };
      }
      return prev;
    });
  };

  const handlePointerUp = (e) => {
    if (finishDrag(e)) return;
    if (!tool) return;
    if (tool === "eraser") {
      drawingRef.current = false;
      lastEraseRef.current = null;
      return;
    }
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onCommitMark?.();
    try {
      captureTarget()?.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        // En select el vacío no bloquea casillas; marcas/texto llevan pointerEvents propios
        pointerEvents: passThroughEmpty ? "none" : active ? "auto" : "none",
        cursor:
          tool === "select"
            ? "default"
            : tool === "eraser"
              ? "cell"
              : tool === "text"
                ? "text"
                : tool
                  ? "crosshair"
                  : "default",
      }}
    >
      <svg
        ref={svgRef}
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        style={{
          display: "block",
          overflow: "visible",
          pointerEvents: passThroughEmpty ? "none" : "auto",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <defs>
          <filter
            id="markSelectedGlow"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="0.012"
              floodColor="#2563eb"
              floodOpacity="0.85"
            />
          </filter>
        </defs>
        {(marks || [])
          .filter((m) => m?.type !== "text")
          .map((m) => (
            <MarkSvgShape
              key={m.id}
              mark={m}
              hit={passThroughEmpty}
              selected={
                selectedMarkId != null &&
                String(m.id) === String(selectedMarkId)
              }
              onPointerDown={
                passThroughEmpty
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const p = toNorm(e.clientX, e.clientY);
                      if (!p) return;
                      beginDrag(m, p, e);
                      onSelectMark?.(m.id);
                    }
                  : undefined
              }
            />
          ))}
        {draftMark && draftMark.type !== "text" ? (
          <MarkSvgShape mark={draftMark} draft />
        ) : null}
      </svg>
      {(marks || [])
        .filter(
          (m) =>
            m?.type === "text" &&
            String(m.text || "").trim() &&
            !(textDraft?.id && String(textDraft.id) === String(m.id)),
        )
        .map((m) => {
          const fs = Number(m.fontSize) || 14;
          const canInteract = idle || tool === "text";
          const isSelected =
            selectedMarkId != null && String(m.id) === String(selectedMarkId);
          return (
            <Box
              key={m.id}
              component="span"
              onPointerDown={(e) => {
                if (!canInteract) return;
                e.preventDefault();
                e.stopPropagation();
                const p = toNorm(e.clientX, e.clientY);
                if (!p) return;
                if (idle || tool === "select") onSelectMark?.(m.id);
                beginDrag(m, p, e);
              }}
              sx={{
                position: "absolute",
                left: `${m.x * 100}%`,
                top: `${m.y * 100}%`,
                transform: "translateY(-70%)",
                color: m.color || DEFAULT_MARK_COLOR,
                fontWeight: 700,
                fontFamily: markFontCss(m.fontFamily),
                fontSize: `${(fs / 14) * 1.85}cqw`,
                lineHeight: 1.15,
                whiteSpace: "pre",
                pointerEvents: canInteract ? "auto" : "none",
                cursor: canInteract
                  ? tool === "text"
                    ? "text"
                    : "move"
                  : "default",
                zIndex: 21,
                userSelect: "none",
                outline: isSelected
                  ? "2px solid"
                  : canInteract
                    ? "1px dashed transparent"
                    : "none",
                outlineColor: isSelected ? "primary.main" : undefined,
                "&:hover": canInteract
                  ? {
                      outline: isSelected ? "2px solid" : "1px dashed",
                      outlineColor: "primary.main",
                      bgcolor: "rgba(255,255,255,0.35)",
                    }
                  : undefined,
              }}
            >
              {m.text}
            </Box>
          );
        })}
      {textDraft ? (
        <Box
          component="input"
          autoFocus
          value={textDraft.value}
          onChange={(e) => onChangeTextDraft?.(e.target.value)}
          onBlur={() => onCommitText?.()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCommitText?.();
            } else if (e.key === "Escape") {
              e.preventDefault();
              onCancelText?.();
            }
          }}
          sx={{
            position: "absolute",
            left: `${textDraft.x * 100}%`,
            top: `${textDraft.y * 100}%`,
            transform: "translateY(-70%)",
            minWidth: 80,
            maxWidth: "45%",
            fontWeight: 700,
            fontFamily: markFontCss(
              textDraft.fontFamily || markFontFamily || "sans",
            ),
            fontSize: `${
              ((Number(textDraft.fontSize) || markFontSize || 14) / 14) * 1.85
            }cqw`,
            color: color,
            bgcolor: "rgba(255,255,255,0.92)",
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: 1,
            px: 0.75,
            py: 0.35,
            outline: "none",
            zIndex: 25,
            pointerEvents: "auto",
          }}
        />
      ) : null}
    </Box>
  );
}

function AssetThumb({
  assetId,
  onClick,
  selected,
  zoom = 1,
  panX = 0.5,
  panY = 0.5,
  fillSlot = false,
  transparentBg = false,
  /** "cover" (default) o "contain" — contain encaja la foto entera en la casilla */
  fit = "cover",
}) {
  const boxRef = useRef(null);
  const [src, setSrc] = useState("");
  const [nat, setNat] = useState(null);
  const [boxPx, setBoxPx] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let revoked = false;
    let url = "";
    setNat(null);
    (async () => {
      try {
        url = await fetchSkillAgentMoodboardAssetBlobUrl(assetId);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [assetId]);

  useEffect(() => {
    if (!fillSlot) return undefined;
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      const r = el.getBoundingClientRect();
      setBoxPx({ w: r.width, h: r.height });
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillSlot, src]);

  const fitted =
    fillSlot && nat && boxPx.w > 1 && boxPx.h > 1
      ? coverFit(
          nat.w,
          nat.h,
          { x: 0, y: 0, w: boxPx.w, h: boxPx.h },
          {
            zoom,
            panX,
            panY,
          },
        )
      : null;

  const objectFit = fit === "contain" ? "contain" : "cover";

  return (
    <Box
      ref={boxRef}
      onClick={(e) => onClick?.(e)}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: transparentBg
          ? fit === "contain"
            ? "#f0f0f0"
            : "transparent"
          : fillSlot
            ? "#ebebeb"
            : "action.hover",
        cursor: onClick ? "pointer" : "default",
        outline: selected ? "2px solid" : "none",
        outlineColor: "primary.main",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt=""
          draggable={false}
          data-slot-img="1"
          onLoad={(e) => {
            const img = e.currentTarget;
            if (img?.naturalWidth && img?.naturalHeight) {
              setNat({ w: img.naturalWidth, h: img.naturalHeight });
            }
          }}
          sx={
            fillSlot && fitted
              ? {
                  position: "absolute",
                  left: fitted.x,
                  top: fitted.y,
                  width: fitted.w,
                  height: fitted.h,
                  objectFit: "fill",
                  display: "block",
                  pointerEvents: "none",
                  userSelect: "none",
                }
              : {
                  width: "100%",
                  height: "100%",
                  objectFit,
                  objectPosition: "center center",
                  display: "block",
                  pointerEvents: "none",
                  opacity: fillSlot ? 0 : 1,
                }
          }
        />
      ) : (
        <Box sx={{ display: "grid", placeItems: "center", height: "100%" }}>
          <CircularProgress size={14} />
        </Box>
      )}
    </Box>
  );
}

function clamp01Local(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0.5;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function normalizeCropUi(rect) {
  let x = clamp01Local(rect.x ?? 0);
  let y = clamp01Local(rect.y ?? 0);
  let w = clamp01Local(rect.w ?? 0.8);
  let h = clamp01Local(rect.h ?? 0.8);
  if (w < 0.05) w = 0.05;
  if (h < 0.05) h = 0.05;
  if (x + w > 1) x = Math.max(0, 1 - w);
  if (y + h > 1) y = Math.max(0, 1 - h);
  return { x, y, w: Math.min(w, 1 - x), h: Math.min(h, 1 - y) };
}

/** Interactive crop dialog — rect in normalized 0–1 of the image. */
function MoodboardCropDialog({ open, assetId, busy, onClose, onConfirm }) {
  const { t } = useTranslation();
  const boxRef = useRef(null);
  const dragRef = useRef(null);
  const [src, setSrc] = useState("");
  const [rect, setRect] = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });

  useEffect(() => {
    if (!open || !assetId) return undefined;
    let revoked = false;
    let url = "";
    setRect({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
    (async () => {
      try {
        url = await fetchSkillAgentMoodboardAssetBlobUrl(assetId);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, assetId]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      const box = boxRef.current;
      if (!d || !box) return;
      const r = box.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      const mx = (e.clientX - r.left) / r.width;
      const my = (e.clientY - r.top) / r.height;
      const dx = mx - d.startMx;
      const dy = my - d.startMy;
      const o = d.orig;
      let next = { ...o };
      if (d.mode === "move") {
        next.x = o.x + dx;
        next.y = o.y + dy;
      } else if (d.mode === "se") {
        next.w = o.w + dx;
        next.h = o.h + dy;
      } else if (d.mode === "nw") {
        next.x = o.x + dx;
        next.y = o.y + dy;
        next.w = o.w - dx;
        next.h = o.h - dy;
      } else if (d.mode === "ne") {
        next.y = o.y + dy;
        next.w = o.w + dx;
        next.h = o.h - dy;
      } else if (d.mode === "sw") {
        next.x = o.x + dx;
        next.w = o.w - dx;
        next.h = o.h + dy;
      }
      setRect(normalizeCropUi(next));
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [open]);

  const startDrag = (mode, e) => {
    e.preventDefault();
    e.stopPropagation();
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    dragRef.current = {
      mode,
      orig: { ...rect },
      startMx: (e.clientX - r.left) / r.width,
      startMy: (e.clientY - r.top) / r.height,
    };
  };

  const handlePts = [
    {
      key: "nw",
      left: `${rect.x * 100}%`,
      top: `${rect.y * 100}%`,
      cursor: "nwse-resize",
    },
    {
      key: "ne",
      left: `${(rect.x + rect.w) * 100}%`,
      top: `${rect.y * 100}%`,
      cursor: "nesw-resize",
    },
    {
      key: "sw",
      left: `${rect.x * 100}%`,
      top: `${(rect.y + rect.h) * 100}%`,
      cursor: "nesw-resize",
    },
    {
      key: "se",
      left: `${(rect.x + rect.w) * 100}%`,
      top: `${(rect.y + rect.h) * 100}%`,
      cursor: "nwse-resize",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={800}>
        {t("skillAgent.moodboard.dialog.cropTitle")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("skillAgent.moodboard.dialog.cropBodyReplace")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            bgcolor: "#111",
            borderRadius: 2,
            overflow: "auto",
            maxHeight: "60vh",
            p: 1,
          }}
        >
          <Box
            ref={boxRef}
            sx={{
              position: "relative",
              display: "inline-block",
              maxWidth: "100%",
              userSelect: "none",
              touchAction: "none",
              lineHeight: 0,
            }}
          >
            {src ? (
              <Box
                component="img"
                src={src}
                alt=""
                draggable={false}
                sx={{
                  display: "block",
                  maxWidth: "100%",
                  maxHeight: "56vh",
                  width: "auto",
                  height: "auto",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <Box
                sx={{ display: "grid", placeItems: "center", py: 8, px: 10 }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {src ? (
              <>
                <Box
                  onPointerDown={(e) => startDrag("move", e)}
                  sx={{
                    position: "absolute",
                    left: `${rect.x * 100}%`,
                    top: `${rect.y * 100}%`,
                    width: `${rect.w * 100}%`,
                    height: `${rect.h * 100}%`,
                    border: "2px solid #fff",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                    cursor: "move",
                    zIndex: 2,
                  }}
                />
                {handlePts.map((h) => (
                  <Box
                    key={h.key}
                    onPointerDown={(e) => startDrag(h.key, e)}
                    sx={{
                      position: "absolute",
                      left: h.left,
                      top: h.top,
                      width: 14,
                      height: 14,
                      ml: "-7px",
                      mt: "-7px",
                      bgcolor: "#fff",
                      border: "1px solid #333",
                      borderRadius: 0.5,
                      cursor: h.cursor,
                      zIndex: 3,
                    }}
                  />
                ))}
              </>
            ) : null}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !src}
          onClick={() => onConfirm?.(normalizeCropUi(rect))}
        >
          {busy
            ? t("skillAgent.moodboard.dialog.cropping")
            : t("skillAgent.moodboard.dialog.cropSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PoolThumb({
  item,
  checked,
  onToggle,
  height = 148,
  alreadyInBoard = false,
}) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");

  useEffect(() => {
    let revoked = false;
    let url = "";
    (async () => {
      try {
        url = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(url);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => {
        if (alreadyInBoard) return;
        onToggle?.(!checked);
      }}
      title={
        alreadyInBoard
          ? t("skillAgent.moodboard.poolAlreadyInBoard")
          : checked
            ? t("skillAgent.moodboard.unmarkSelection")
            : t("skillAgent.moodboard.markToAdd")
      }
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: checked
          ? "primary.main"
          : alreadyInBoard
            ? "transparent"
            : "divider",
        cursor: alreadyInBoard ? "default" : "pointer",
        bgcolor: "#eee",
        height,
        minHeight: height,
        maxHeight: height,
        opacity: alreadyInBoard ? 0.45 : 1,
      }}
    >
      {!alreadyInBoard ? (
        <Checkbox
          size="small"
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle?.(e.target.checked)}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.25,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      ) : (
        <Chip
          size="small"
          label={t("skillAgent.moodboard.onBoardChip")}
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 2,
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 700,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      )}
      {src ? (
        <Box
          component="img"
          src={src}
          alt={item.Title || item.BrandName || ""}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          px: 0.75,
          py: 0.4,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.55)",
          lineHeight: 1.25,
        }}
      >
        {item.BrandName || item.Title || `#${item.id}`}
        {item.SearchTitulo ? ` · ${item.SearchTitulo}` : ""}
      </Typography>
    </Box>
  );
}

/** Dialog: marcar fotos de SEARCH del cliente/temporada y añadirlas al board. */
function MoodboardAddPhotosDialog({
  open,
  onClose,
  busy,
  pool,
  poolLoading,
  poolError,
  usedSearchItemIds,
  pick,
  setPick,
  onConfirm,
  seasonLabel,
  clientLabel,
}) {
  const { t } = useTranslation();
  const [searchFilter, setSearchFilter] = useState("all");
  const [showCount, setShowCount] = useState(36);

  useEffect(() => {
    if (!open) return;
    setSearchFilter("all");
    setShowCount(36);
  }, [open]);

  const searches = pool?.searches || [];
  const items = useMemo(() => {
    const list = pool?.items || [];
    if (searchFilter === "all") return list;
    return list.filter((it) => Number(it.SearchId) === Number(searchFilter));
  }, [pool?.items, searchFilter]);

  const pickCount = pick?.size || 0;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={800} component="div">
          {t("skillAgent.moodboard.dialog.addPhotosTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          SEARCH de{" "}
          <strong>
            {clientLabel ||
              t("skillAgent.moodboard.dialog.addPhotosClientFallback")}
          </strong>
          {seasonLabel ? (
            <>
              {" "}
              · <strong>{seasonLabel}</strong>
            </>
          ) : null}
          . {t("skillAgent.moodboard.dialog.addPhotosIntroTail")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {poolError ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {poolError}
          </Alert>
        ) : null}
        {searches.length > 1 ? (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5 }}
          >
            <Chip
              size="small"
              label={t("skillAgent.moodboard.dialog.filterAll")}
              color={searchFilter === "all" ? "primary" : "default"}
              variant={searchFilter === "all" ? "filled" : "outlined"}
              onClick={() => setSearchFilter("all")}
              sx={{ fontWeight: 700 }}
            />
            {searches.map((s) => (
              <Chip
                key={s.id}
                size="small"
                label={
                  s.Titulo ||
                  t("skillAgent.moodboard.dialog.searchChip", { id: s.id })
                }
                color={
                  Number(searchFilter) === Number(s.id) ? "primary" : "default"
                }
                variant={
                  Number(searchFilter) === Number(s.id) ? "filled" : "outlined"
                }
                onClick={() => setSearchFilter(String(s.id))}
                sx={{ fontWeight: 700, maxWidth: 180 }}
              />
            ))}
          </Stack>
        ) : null}

        {poolLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !items.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              {t("skillAgent.moodboard.dialog.addPhotosEmpty")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.addPhotosEmptyHint")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 1,
              }}
            >
              {items.slice(0, showCount).map((it) => {
                const already = usedSearchItemIds.has(Number(it.id));
                return (
                  <PoolThumb
                    key={it.id}
                    item={it}
                    height={140}
                    alreadyInBoard={already}
                    checked={!already && pick.has(Number(it.id))}
                    onToggle={(on) => {
                      setPick((prev) => {
                        const next = new Set(prev);
                        if (on) next.add(Number(it.id));
                        else next.delete(Number(it.id));
                        return next;
                      });
                    }}
                  />
                );
              })}
            </Box>
            {items.length > showCount ? (
              <Button
                size="small"
                sx={{ mt: 1.5, textTransform: "none" }}
                onClick={() => setShowCount((n) => n + 36)}
              >
                {t("skillAgent.moodboard.dialog.loadMore", {
                  n: items.length - showCount,
                })}
              </Button>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("skillAgent.common.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || pickCount === 0}
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          {busy
            ? t("skillAgent.moodboard.dialog.adding")
            : pickCount
              ? t("skillAgent.moodboard.dialog.addN", { n: pickCount })
              : t("skillAgent.moodboard.dialog.add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Dialog: fotos ya en el board — marcar, colocar en página, quitar. */
function MoodboardBoardPhotosDialog({
  open,
  onClose,
  assets,
  selectedIds,
  usedOnPagesIds,
  onToggleSelect,
  onClearSelect,
  onPlaceOne,
  onPlaceSelected,
  onRemove,
  pageLabel,
  busy,
}) {
  const { t } = useTranslation();
  const selected = selectedIds instanceof Set ? selectedIds : new Set();
  const usedOnPages =
    usedOnPagesIds instanceof Set ? usedOnPagesIds : new Set();
  const nSel = selected.size;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
        >
          <Box>
            <Typography variant="h6" fontWeight={800} component="div">
              {t("skillAgent.moodboard.dialog.boardPhotosTitle", {
                n: assets.length,
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.boardPhotosHintLong", {
                page: pageLabel,
              })}
            </Typography>
          </Box>
          {nSel > 0 ? (
            <Chip
              size="small"
              color="primary"
              label={t("skillAgent.moodboard.dialog.selectedChip", {
                n: nSel,
              })}
              onDelete={onClearSelect}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1.5 }}>
        {!assets.length ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography fontWeight={700} color="text.secondary">
              {t("skillAgent.moodboard.dialog.boardPhotosEmpty")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.boardPhotosEmptyHint")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gap: 1.25,
            }}
          >
            {assets.map((a) => {
              const id = Number(a.id);
              const isSelected = selected.has(id);
              const alreadyOnPage = usedOnPages.has(id);
              const isCutout =
                String(a.Kind || "").toLowerCase() === "cutout" ||
                /\(\s*sin fondo\s*\)/i.test(String(a.Title || ""));
              return (
                <Box
                  key={a.id}
                  title={
                    alreadyOnPage
                      ? t("skillAgent.moodboard.alreadyOnPageTitle")
                      : undefined
                  }
                  sx={{
                    position: "relative",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "2px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: "#f3f3f3",
                    aspectRatio: "1 / 1.15",
                    opacity: alreadyOnPage ? 0.45 : 1,
                    boxShadow: isSelected
                      ? `0 0 0 1px ${alpha("#0ea5e9", 0.35)}`
                      : "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      cursor: "pointer",
                    }}
                    onClick={() => onPlaceOne?.(a.id)}
                  >
                    <AssetThumb
                      assetId={a.id}
                      selected={isSelected}
                      transparentBg
                      fit="contain"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label={
                      isSelected
                        ? t("skillAgent.moodboard.unmarkSelection")
                        : t("skillAgent.moodboard.markPhoto")
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelect?.(a.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      bgcolor: alpha("#fff", 0.95),
                      boxShadow: 1,
                      color: isSelected ? "primary.main" : "text.secondary",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    {isSelected ? (
                      <CheckBoxOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={t("skillAgent.moodboard.removePhotoAria")}
                    disabled={busy}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.(a.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      zIndex: 2,
                      width: 30,
                      height: 30,
                      bgcolor: alpha("#fff", 0.95),
                      boxShadow: 1,
                      color: "error.main",
                      "&:hover": { bgcolor: "#fff" },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  {isCutout ? (
                    <Chip
                      size="small"
                      label={t("skillAgent.moodboard.dialog.cutoutChip")}
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        zIndex: 2,
                        height: 22,
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        bgcolor: alpha("#fff", 0.92),
                      }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("skillAgent.common.close")}
        </Button>
        <Button
          variant="contained"
          disabled={!nSel}
          startIcon={<DashboardCustomizeOutlinedIcon />}
          onClick={onPlaceSelected}
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          {nSel
            ? t("skillAgent.moodboard.dialog.placeN", {
                n: nSel,
                p: pageLabel,
              })
            : t("skillAgent.moodboard.dialog.placeOne", { p: pageLabel })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function slotsBBox(slots) {
  const list = Array.isArray(slots) ? slots : [];
  if (!list.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const s of list) {
    x0 = Math.min(x0, Number(s.x) || 0);
    y0 = Math.min(y0, Number(s.y) || 0);
    x1 = Math.max(x1, (Number(s.x) || 0) + (Number(s.w) || 0));
    y1 = Math.max(y1, (Number(s.y) || 0) + (Number(s.h) || 0));
  }
  return { x: x0, y: y0, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
}

function scaleSlotsWithBBox(origins, bbox0, bbox1) {
  const sx = bbox0.w > 1e-6 ? bbox1.w / bbox0.w : 1;
  const sy = bbox0.h > 1e-6 ? bbox1.h / bbox0.h : 1;
  return origins.map((o) =>
    snapSlot(
      clampSlot({
        ...o,
        x: bbox1.x + (o.x - bbox0.x) * sx,
        y: bbox1.y + (o.y - bbox0.y) * sy,
        w: Math.max(MIN_SLOT, o.w * sx),
        h: Math.max(MIN_SLOT, o.h * sy),
      }),
    ),
  );
}

function rectsIntersect(a, b) {
  return !(
    a.x + a.w < b.x ||
    b.x + b.w < a.x ||
    a.y + a.h < b.y ||
    b.y + b.h < a.y
  );
}

function FreeformSlotBox({
  slot,
  selected,
  multiSelected = false,
  onSelect,
  onChange,
  onGroupMoveStart,
  onGroupMove,
  onGroupMoveEnd,
  onRequestRemove,
  canRemove,
  canvasRef,
  hideChrome = false,
}) {
  const { t } = useTranslation();
  const dragRef = useRef(null);
  const rootRef = useRef(null);
  const slotRef = useRef(slot);
  const selectedRef = useRef(selected);
  slotRef.current = slot;
  selectedRef.current = selected;

  const emitSlot = (partial, { snap = true } = {}) => {
    const next = clampSlot({
      ...slot,
      ...partial,
      id: slot.id,
      assetId: slot.assetId,
    });
    onChange?.(snap ? snapSlot(next) : next);
  };

  /* Alt+rueda: passive:false para que preventDefault pare el scroll de página */
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onWheelNative = (e) => {
      const s = slotRef.current;
      if (!selectedRef.current || !s?.assetId || !e.altKey) return;
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.12 : 0.12;
      const zoom = Math.min(8, Math.max(0.25, (Number(s.zoom) || 1) + delta));
      onChange?.(
        clampSlot({
          ...s,
          id: s.id,
          assetId: s.assetId,
          zoom,
        }),
      );
    };
    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => el.removeEventListener("wheel", onWheelNative);
  }, [onChange]);

  const startGesture = (mode, handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Illustrator: solo Shift suma/quita; Ctrl/Cmd no (evita doble-toggle raro)
    const additive = Boolean(e.shiftKey);
    if (additive) {
      onSelect?.(slot.id, { additive: true });
    } else if (!selected) {
      onSelect?.(slot.id, { additive: false });
    } else if (multiSelected) {
      // Clic en un miembro del grupo: mantener grupo y activar esta casilla
      onSelect?.(slot.id, { additive: false });
    }
    // Si ya es la única seleccionada, no re-disparar selección
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const slotRect = e.currentTarget.getBoundingClientRect();
    const imgEl = e.currentTarget.querySelector("img[data-slot-img]");
    const iw = Math.max(1, imgEl?.naturalWidth || 1);
    const ih = Math.max(1, imgEl?.naturalHeight || 1);
    // Si ya hay grupo seleccionado, mover todos; el Shift+clic solo cambia la selección
    const useGroupMove =
      mode === "move" && multiSelected && typeof onGroupMove === "function";
    if (useGroupMove) onGroupMoveStart?.();
    dragRef.current = {
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      orig: { ...slot },
      cw: rect.width,
      ch: rect.height,
      sw: Math.max(1, slotRect.width),
      sh: Math.max(1, slotRect.height),
      iw,
      ih,
      useGroupMove,
      moved: false,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dist = Math.hypot(ev.clientX - d.startX, ev.clientY - d.startY);
      if (dist > 3) d.moved = true;
      let next = { ...d.orig };
      if (d.mode === "pan") {
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const box = { x: 0, y: 0, w: d.sw, h: d.sh };
        const fitted0 = coverFit(d.iw, d.ih, box, {
          zoom: d.orig.zoom,
          panX: d.orig.panX,
          panY: d.orig.panY,
        });
        const newX = fitted0.x + dx;
        const newY = fitted0.y + dy;
        const denomW = box.w - fitted0.w;
        const denomH = box.h - fitted0.h;
        next.panX =
          Math.abs(denomW) < 0.0001
            ? 0.5
            : clamp01Local((newX - box.x) / denomW);
        next.panY =
          Math.abs(denomH) < 0.0001
            ? 0.5
            : clamp01Local((newY - box.y) / denomH);
        onChange?.(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        );
        return;
      }
      if (d.mode === "move") {
        const dx = (ev.clientX - d.startX) / d.cw;
        const dy = (ev.clientY - d.startY) / d.ch;
        if (d.useGroupMove) {
          onGroupMove?.(dx, dy);
          return;
        }
        next.x = d.orig.x + dx;
        next.y = d.orig.y + dy;
        // Sin snap en cada frame: el grupo/casilla no «salta»
        onChange?.(
          clampSlot({
            ...next,
            id: slot.id,
            assetId: slot.assetId,
          }),
        );
        return;
      }
      const dx = (ev.clientX - d.startX) / d.cw;
      const dy = (ev.clientY - d.startY) / d.ch;
      const h = d.handle;
      if (h.includes("e")) next.w = d.orig.w + dx;
      if (h.includes("s")) next.h = d.orig.h + dy;
      if (h.includes("w")) {
        next.x = d.orig.x + dx;
        next.w = d.orig.w - dx;
      }
      if (h.includes("n")) {
        next.y = d.orig.y + dy;
        next.h = d.orig.h - dy;
      }
      onChange?.(
        clampSlot({
          ...next,
          id: slot.id,
          assetId: slot.assetId,
        }),
      );
    };
    const onUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (!d?.moved) return;
      if (d.mode === "move" && d.useGroupMove) {
        onGroupMoveEnd?.();
        return;
      }
      if (d.mode === "move" || d.handle) {
        onChange?.(
          snapSlot(
            clampSlot({
              ...slotRef.current,
              id: slot.id,
              assetId: slot.assetId,
            }),
          ),
        );
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const bumpZoom = (dir) => {
    const zoom = Math.min(
      8,
      Math.max(0.25, (Number(slot.zoom) || 1) + dir * 0.25),
    );
    emitSlot({ zoom }, { snap: false });
  };

  const pinchDist = (t0, t1) => {
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e) => {
    if (!slot.assetId || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(slot.id, { additive: false });
    dragRef.current = {
      mode: "pinch",
      orig: { ...slot },
      startDist: pinchDist(e.touches[0], e.touches[1]),
    };
  };

  const onTouchMove = (e) => {
    const d = dragRef.current;
    if (!d || d.mode !== "pinch" || e.touches.length !== 2) return;
    e.preventDefault();
    e.stopPropagation();
    const dist = pinchDist(e.touches[0], e.touches[1]);
    if (!d.startDist || d.startDist < 1) return;
    const ratio = dist / d.startDist;
    const zoom = Math.min(
      8,
      Math.max(0.25, (Number(d.orig.zoom) || 1) * ratio),
    );
    onChange?.(
      clampSlot({
        ...d.orig,
        id: slot.id,
        assetId: slot.assetId,
        zoom,
      }),
    );
  };

  const onTouchEnd = () => {
    if (dragRef.current?.mode === "pinch") dragRef.current = null;
  };

  const isCircle = String(slot.shape || "rect") === "circle";
  const hasAsset = Boolean(slot.assetId);
  const showChrome = selected && !hideChrome;

  return (
    <Box
      ref={rootRef}
      onPointerDown={(e) => {
        if (e.pointerType === "touch" && dragRef.current?.mode === "pinch") {
          return;
        }
        if (slot.assetId && e.altKey) {
          startGesture("pan", null, e);
        } else {
          startGesture("move", null, e);
        }
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      sx={{
        position: "absolute",
        left: `${slot.x * 100}%`,
        top: `${slot.y * 100}%`,
        width: `${slot.w * 100}%`,
        height: `${slot.h * 100}%`,
        bgcolor: hasAsset ? "transparent" : "transparent",
        overflow: "hidden",
        borderRadius: isCircle ? "50%" : 0,
        outline: selected
          ? "2px solid"
          : hasAsset
            ? "1px solid transparent"
            : "1px dashed rgba(0,0,0,0.28)",
        outlineColor: selected ? "primary.main" : undefined,
        outlineOffset: -1,
        cursor: "move",
        zIndex: selected
          ? 1000 + (Number(slot.z) || 0)
          : 1 + (Number(slot.z) || 0),
        touchAction: "none",
        userSelect: "none",
        "&:active": {
          cursor: selected && slot.assetId ? "grabbing" : "move",
        },
        "&:hover .slot-remove, &:hover .slot-zoom": { opacity: 1 },
      }}
    >
      {slot.assetId ? (
        <AssetThumb
          assetId={slot.assetId}
          fillSlot
          transparentBg
          zoom={slot.zoom}
          panX={slot.panX}
          panY={slot.panY}
        />
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "grid",
            placeItems: "center",
            height: "100%",
            fontSize: "0.65rem",
            pointerEvents: "none",
          }}
        >
          {isCircle ? "○ vacía" : "vacía"}
        </Typography>
      )}
      {showChrome && slot.assetId ? (
        <Stack
          className="slot-zoom"
          direction="row"
          spacing={0.25}
          sx={{
            position: "absolute",
            left: 4,
            bottom: 4,
            zIndex: 7,
            opacity: 1,
            bgcolor: alpha("#0f172a", 0.78),
            borderRadius: 1.5,
            p: 0.35,
          }}
        >
          <Tooltip title={t("skillAgent.moodboard.zoomOut")}>
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                bumpZoom(-1);
              }}
              disabled={(slot.zoom || 1) <= 0.25}
              sx={{ color: "#fff", p: 0.75, minWidth: 36, minHeight: 36 }}
            >
              <ZoomOutOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              fontWeight: 700,
              px: 0.5,
              alignSelf: "center",
              fontSize: "0.75rem",
              minWidth: 36,
              textAlign: "center",
            }}
          >
            {Math.round((slot.zoom || 1) * 100)}%
          </Typography>
          <Tooltip title={t("skillAgent.moodboard.zoomIn")}>
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                bumpZoom(1);
              }}
              disabled={(slot.zoom || 1) >= 8}
              sx={{ color: "#fff", p: 0.75, minWidth: 36, minHeight: 36 }}
            >
              <ZoomInOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) : null}
      {canRemove && showChrome ? (
        <Tooltip title={t("skillAgent.moodboard.removeSlot")}>
          <IconButton
            className="slot-remove"
            size="small"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onRequestRemove?.(slot.id);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              zIndex: 7,
              opacity: 1,
              transition: "opacity 0.12s",
              bgcolor: alpha("#fff", 0.92),
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              p: 0.35,
              "&:hover": { bgcolor: "#fff", color: "error.main" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {showChrome
        ? HANDLES.map((h) => (
            <Box
              key={h}
              onPointerDown={(e) => startGesture("resize", h, e)}
              sx={{
                position: "absolute",
                width: 10,
                height: 10,
                bgcolor: "primary.main",
                border: "1px solid #fff",
                borderRadius: 0.25,
                zIndex: 6,
                ...(h.includes("n") ? { top: -5 } : null),
                ...(h.includes("s") ? { bottom: -5 } : null),
                ...(h.includes("w") ? { left: -5 } : null),
                ...(h.includes("e") ? { right: -5 } : null),
                ...(h === "n" || h === "s"
                  ? { left: "50%", marginLeft: "-5px" }
                  : null),
                ...(h === "e" || h === "w"
                  ? { top: "50%", marginTop: "-5px" }
                  : null),
                cursor: `${h}-resize`,
              }}
            />
          ))
        : null}
    </Box>
  );
}

/** Marco de transformación cuando hay varias casillas seleccionadas. */
function GroupTransformBox({ slots, canvasRef, onScale }) {
  const bbox = useMemo(() => slotsBBox(slots), [slots]);
  const dragRef = useRef(null);
  if (!bbox || bbox.w < 0.001 || bbox.h < 0.001) return null;

  const startResize = (handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const origins = slots.map((s) => ({ ...s }));
    const bbox0 = { ...bbox };
    dragRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      cw: rect.width,
      ch: rect.height,
      origins,
      bbox0,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = (ev.clientX - d.startX) / d.cw;
      const dy = (ev.clientY - d.startY) / d.ch;
      const h = d.handle;
      let x = d.bbox0.x;
      let y = d.bbox0.y;
      let w = d.bbox0.w;
      let hgt = d.bbox0.h;
      if (h.includes("e")) w = d.bbox0.w + dx;
      if (h.includes("s")) hgt = d.bbox0.h + dy;
      if (h.includes("w")) {
        x = d.bbox0.x + dx;
        w = d.bbox0.w - dx;
      }
      if (h.includes("n")) {
        y = d.bbox0.y + dy;
        hgt = d.bbox0.h - dy;
      }
      if (w < MIN_SLOT) {
        if (h.includes("w")) x = d.bbox0.x + d.bbox0.w - MIN_SLOT;
        w = MIN_SLOT;
      }
      if (hgt < MIN_SLOT) {
        if (h.includes("n")) y = d.bbox0.y + d.bbox0.h - MIN_SLOT;
        hgt = MIN_SLOT;
      }
      onScale?.(scaleSlotsWithBBox(d.origins, d.bbox0, { x, y, w, h: hgt }));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <Box
      sx={{
        position: "absolute",
        left: `${bbox.x * 100}%`,
        top: `${bbox.y * 100}%`,
        width: `${bbox.w * 100}%`,
        height: `${bbox.h * 100}%`,
        outline: "2px dashed",
        outlineColor: "primary.main",
        outlineOffset: 2,
        pointerEvents: "none",
        zIndex: 1200,
      }}
    >
      {HANDLES.map((h) => (
        <Box
          key={h}
          onPointerDown={(e) => startResize(h, e)}
          sx={{
            position: "absolute",
            width: 12,
            height: 12,
            bgcolor: "primary.main",
            border: "2px solid #fff",
            borderRadius: 0.25,
            pointerEvents: "auto",
            zIndex: 1201,
            ...(h.includes("n") ? { top: -6 } : null),
            ...(h.includes("s") ? { bottom: -6 } : null),
            ...(h.includes("w") ? { left: -6 } : null),
            ...(h.includes("e") ? { right: -6 } : null),
            ...(h === "n" || h === "s"
              ? { left: "50%", marginLeft: "-6px" }
              : null),
            ...(h === "e" || h === "w"
              ? { top: "50%", marginTop: "-6px" }
              : null),
            cursor: `${h}-resize`,
          }}
        />
      ))}
    </Box>
  );
}

async function loadPoolFromSearches(board) {
  const customerId = board?.CustomerId ? Number(board.CustomerId) : null;
  const temporada = String(board?.Temporada || "").trim();
  const list = await listSkillAgentSearches({
    ...(customerId ? { customerId } : {}),
    ...(temporada ? { temporada } : {}),
  });
  const searches = (Array.isArray(list) ? list : []).filter((s) => {
    if (customerId && Number(s.CustomerId) !== customerId) return false;
    if (temporada && String(s.Temporada || "").trim() !== temporada) {
      return false;
    }
    return true;
  });
  const items = [];
  for (const s of searches) {
    const detail = await getSkillAgentSearchById(s.id);
    const imgs = (Array.isArray(detail?.items) ? detail.items : []).filter(
      (it) => !it.Kind || String(it.Kind).toLowerCase() === "image",
    );
    for (const it of imgs) {
      items.push({
        id: it.id,
        SearchId: s.id,
        Title: it.Title,
        BrandName: it.BrandName,
        BrandId: it.BrandId,
        SourceUrl: it.SourceUrl,
        MimeType: it.MimeType,
        DriveFileUrl: it.DriveFileUrl,
        SearchTitulo: s.Titulo || null,
      });
    }
  }
  return {
    searches: searches.map((s) => ({
      id: s.id,
      Titulo: s.Titulo,
      Status: s.Status,
      Temporada: s.Temporada,
      CustomerId: s.CustomerId,
    })),
    items,
  };
}

export default function SkillAgentMoodboardDetail({
  moodboardId,
  onChanged,
  onDeleted,
  onOpenHelp,
  viewerName = "",
  viewerUserId = null,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const skipAutosaveRef = useRef(true);
  const pagesRef = useRef([emptyFreePage()]);
  const metaRef = useRef({
    titulo: "",
    categoryLabel: "",
    studioLine: "",
    chromeStyle: normalizeChromeStyle(null),
  });
  const expectedUpdatedAtRef = useRef(null);
  const saveStateRef = useRef("saved");
  const saveChainRef = useRef(Promise.resolve());
  const ignoreOwnSocketUntilRef = useRef(0);
  const pagesTimerRef = useRef(null);
  const metaTimerRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  const softRefreshBusyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | unsaved | error
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [peerViewers, setPeerViewers] = useState([]);
  const [staleConflict, setStaleConflict] = useState(false);
  const [board, setBoard] = useState(null);
  const [assets, setAssets] = useState([]);
  const [pages, setPages] = useState([emptyFreePage()]);
  const [pageIdx, setPageIdx] = useState(0);
  const [viewZoom, setViewZoom] = useState(1);
  const viewScrollRef = useRef(null);
  const [selectedSlotIds, setSelectedSlotIds] = useState(() => new Set());
  const [activeSlotId, setActiveSlotId] = useState(null);
  const groupMoveOriginsRef = useRef(null);
  const selectedSlotIdsRef = useRef(selectedSlotIds);
  selectedSlotIdsRef.current = selectedSlotIds;
  const [marquee, setMarquee] = useState(null);
  const [markTool, setMarkTool] = useState(null); // null | select|pen|line|arrow|ellipse|rect|text|eraser
  const [selectedMarkId, setSelectedMarkId] = useState(null);
  const [markColor, setMarkColor] = useState(DEFAULT_MARK_COLOR);
  const [markStroke, setMarkStroke] = useState(2);
  const [markFontSize, setMarkFontSize] = useState(14);
  const [markFontFamily, setMarkFontFamily] = useState("sans");
  const [draftMark, setDraftMark] = useState(null);
  const [textDraft, setTextDraft] = useState(null);
  const draftMarkRef = useRef(null);
  draftMarkRef.current = draftMark;
  const [pool, setPool] = useState({ searches: [], items: [] });
  const [poolPick, setPoolPick] = useState(() => new Set());
  const [poolError, setPoolError] = useState("");
  const [poolLoading, setPoolLoading] = useState(false);
  const [addPhotosOpen, setAddPhotosOpen] = useState(false);
  const [boardPhotosOpen, setBoardPhotosOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [slotRemoveId, setSlotRemoveId] = useState(null);
  const [assetRemoveId, setAssetRemoveId] = useState(null);
  const [pageRemoveOpen, setPageRemoveOpen] = useState(false);
  const [aviatorConfirmOpen, setAviatorConfirmOpen] = useState(false);
  const [autoPagesConfirmOpen, setAutoPagesConfirmOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [removeBgConfirmOpen, setRemoveBgConfirmOpen] = useState(false);
  const [gallerySelected, setGallerySelected] = useState(() => new Set());
  const [titulo, setTitulo] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [studioLine, setStudioLine] = useState("");
  const [chromeStyle, setChromeStyle] = useState(() =>
    normalizeChromeStyle(null),
  );
  const [metaOpen, setMetaOpen] = useState(false);
  const [layoutMenuEl, setLayoutMenuEl] = useState(null);
  const [strokeAnchor, setStrokeAnchor] = useState(null);
  const [strokeWidth, setStrokeWidth] = useState(6);
  const [strokeColor, setStrokeColor] = useState("#000000");
  const [cropOpen, setCropOpen] = useState(false);

  const load = useCallback(async () => {
    if (!moodboardId) return;
    skipAutosaveRef.current = true;
    setLoading(true);
    setError("");
    setPoolError("");
    setSaveState("saved");
    try {
      const data = await getSkillAgentMoodboardById(moodboardId);
      setBoard(data);
      if (data?.updatedAt) expectedUpdatedAtRef.current = data.updatedAt;
      setStaleConflict(false);
      setPeerViewers([]);
      setAssets(Array.isArray(data?.assets) ? data.assets : []);
      setPages(normalizePages(data?.PagesJson));
      setTitulo(data?.Titulo || "");
      setCategoryLabel(data?.CategoryLabel || "");
      setStudioLine(data?.StudioLine || "");
      setChromeStyle(normalizeChromeStyle(data?.ChromeStyleJson));
      setActiveSlotId(null);
      setSelectedSlotIds(new Set());

      setPoolLoading(true);
      let poolData = { searches: [], items: [] };
      try {
        poolData = await getSkillAgentMoodboardPool(moodboardId);
      } catch (poolErr) {
        try {
          poolData = await loadPoolFromSearches(data);
        } catch {
          setPoolError(
            poolErr?.response?.data?.error ||
              t("skillAgent.moodboard.error.poolSearch"),
          );
          poolData = { searches: [], items: [] };
        }
      }
      if (
        !Array.isArray(poolData?.items) ||
        (!poolData.items.length && data?.CustomerId)
      ) {
        try {
          const fallback = await loadPoolFromSearches(data);
          if (fallback.items?.length) poolData = fallback;
        } catch {
          /* keep primary */
        }
      }
      setPool({
        searches: Array.isArray(poolData?.searches) ? poolData.searches : [],
        items: Array.isArray(poolData?.items) ? poolData.items : [],
      });
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.load"),
      );
    } finally {
      setPoolLoading(false);
      setLoading(false);
      // Enable autosave after React applies the loaded state.
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 100);
    }
  }, [moodboardId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onChangedRef.current = onChanged;
  }, [onChanged]);

  const usedSearchItemIds = useMemo(() => {
    const s = new Set();
    for (const a of assets) {
      if (a.SearchItemId) s.add(Number(a.SearchItemId));
    }
    return s;
  }, [assets]);

  const usedOnPagesAssetIds = useMemo(() => {
    const s = new Set();
    for (const p of pages || []) {
      for (const slot of p?.slots || []) {
        if (slot?.assetId != null) s.add(Number(slot.assetId));
      }
    }
    return s;
  }, [pages]);

  const currentPage = useMemo(
    () => normalizeFreePage(pages[pageIdx] || emptyFreePage()),
    [pages, pageIdx],
  );
  const activeSlot = useMemo(
    () => (currentPage.slots || []).find((s) => s.id === activeSlotId) || null,
    [currentPage, activeSlotId],
  );
  const activeAsset = useMemo(() => {
    if (!activeSlot?.assetId) return null;
    return (
      assets.find((a) => Number(a.id) === Number(activeSlot.assetId)) || null
    );
  }, [assets, activeSlot]);
  const activeAssetIsCutout = useMemo(() => {
    if (!activeAsset) return false;
    const kind = String(activeAsset.Kind || "").toLowerCase();
    if (kind === "cutout") return true;
    return /\(\s*sin fondo\s*\)/i.test(String(activeAsset.Title || ""));
  }, [activeAsset]);
  const layeredSlots = useMemo(
    () => sortSlotsByLayer(currentPage.slots || []),
    [currentPage],
  );

  // Ctrl/Cmd + rueda: zoom de vista (listener no-passive para poder preventDefault)
  useEffect(() => {
    const el = viewScrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const dir = e.deltaY > 0 ? -1 : 1;
      setViewZoom((z) => clampPageViewZoom(z + dir * PAGE_VIEW_ZOOM_STEP));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const applyBoard = (data, { fromServer = true } = {}) => {
    if (!data) return;
    if (fromServer) skipAutosaveRef.current = true;
    setBoard(data);
    if (data.updatedAt) expectedUpdatedAtRef.current = data.updatedAt;
    if (Array.isArray(data.assets)) setAssets(data.assets);
    if (data.PagesJson) setPages(normalizePages(data.PagesJson));
    if (data.Titulo != null) setTitulo(data.Titulo);
    if (data.CategoryLabel != null) setCategoryLabel(data.CategoryLabel);
    if (data.StudioLine != null) setStudioLine(data.StudioLine);
    if (data.ChromeStyleJson != null) {
      setChromeStyle(normalizeChromeStyle(data.ChromeStyleJson));
    }
    if (fromServer) {
      setTimeout(() => {
        skipAutosaveRef.current = false;
      }, 80);
    }
  };

  const updateCurrentPageSlots = (updater) => {
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        const slots =
          typeof updater === "function" ? updater(base.slots) : updater;
        return {
          ...base,
          slots: slots.map((s) => clampSlot(s)),
          marks: base.marks || [],
        };
      }),
    );
  };

  const updateCurrentPageMarks = (updater) => {
    setPages((prev) =>
      prev.map((p, i) => {
        if (i !== pageIdx) return p;
        const base = normalizeFreePage(p);
        const marks =
          typeof updater === "function" ? updater(base.marks || []) : updater;
        return {
          ...base,
          slots: base.slots,
          marks: (Array.isArray(marks) ? marks : []).map((m) => clampMark(m)),
        };
      }),
    );
  };

  const handleDraftMark = useCallback((next) => {
    if (typeof next === "function") {
      setDraftMark((prev) => next(prev));
    } else {
      setDraftMark(next);
    }
  }, []);

  const handleCommitDraftMark = useCallback(() => {
    const draft = draftMarkRef.current;
    if (!draft) return;
    if (draft.type === "path" && (draft.points || []).length < 2) {
      setDraftMark(null);
      return;
    }
    if (
      (draft.type === "line" || draft.type === "arrow") &&
      Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1) < 0.008
    ) {
      setDraftMark(null);
      return;
    }
    if (
      (draft.type === "ellipse" || draft.type === "rect") &&
      (draft.w < 0.008 || draft.h < 0.008)
    ) {
      setDraftMark(null);
      return;
    }
    const { _ox, _oy, _shift, ...clean } = draft;
    updateCurrentPageMarks((marks) => [
      ...marks,
      clampMark({ ...clean, id: newMarkId() }),
    ]);
    setDraftMark(null);
  }, [pageIdx]);

  const handleEraseAt = useCallback(
    (x, y) => {
      updateCurrentPageMarks((marks) =>
        marks.filter((m) => !markHitTest(m, x, y, 0.025)),
      );
    },
    [pageIdx],
  );

  const handleEraseAlong = useCallback(
    (from, to) => {
      if (!to) return;
      updateCurrentPageMarks((marks) =>
        marks.filter((m) => {
          if (markHitTest(m, to.x, to.y, 0.028)) return false;
          if (from && markHitTest(m, from.x, from.y, 0.028)) return false;
          return true;
        }),
      );
    },
    [pageIdx],
  );

  const patchSelectedMark = useCallback(
    (patch) => {
      if (!selectedMarkId) return;
      updateCurrentPageMarks((marks) =>
        marks.map((m) =>
          String(m.id) === String(selectedMarkId)
            ? clampMark({ ...m, ...patch, id: m.id })
            : m,
        ),
      );
    },
    [pageIdx, selectedMarkId],
  );

  const handleSelectMark = useCallback(
    (id) => {
      setSelectedMarkId(id ?? null);
      if (id == null) return;
      const mark = (pagesRef.current[pageIdx]?.marks || []).find(
        (m) => String(m.id) === String(id),
      );
      if (!mark) return;
      if (mark.color) setMarkColor(mark.color);
      if (mark.stroke != null) setMarkStroke(Number(mark.stroke) || 2);
      if (mark.type === "text") {
        setMarkFontSize(Number(mark.fontSize) || 14);
        setMarkFontFamily(normalizeMarkFontFamily(mark.fontFamily));
      }
    },
    [pageIdx],
  );

  const handleMoveMark = useCallback(
    (id, nextMark) => {
      if (!id || !nextMark) return;
      updateCurrentPageMarks((marks) =>
        marks.map((m) =>
          String(m.id) === String(id)
            ? clampMark({ ...m, ...nextMark, id: m.id })
            : m,
        ),
      );
    },
    [pageIdx],
  );

  const clearSlotSelection = useCallback(() => {
    setSelectedSlotIds(new Set());
    setActiveSlotId(null);
  }, []);

  const selectOnlySlot = useCallback((id) => {
    setSelectedMarkId(null);
    if (!id) {
      setSelectedSlotIds(new Set());
      setActiveSlotId(null);
      return;
    }
    setSelectedSlotIds(new Set([id]));
    setActiveSlotId(id);
  }, []);

  const handleSelectSlot = useCallback((id, { additive = false } = {}) => {
    setSelectedMarkId(null);
    if (!id) return;
    setSelectedSlotIds((prev) => {
      if (additive) {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
          setActiveSlotId((cur) => {
            if (cur === id) return next.size ? [...next][next.size - 1] : null;
            return cur && next.has(cur) ? cur : [...next][0] || null;
          });
        } else {
          next.add(id);
          setActiveSlotId(id);
        }
        return next;
      }
      if (prev.has(id) && prev.size > 1) {
        setActiveSlotId(id);
        return prev;
      }
      setActiveSlotId(id);
      return new Set([id]);
    });
  }, []);

  const selectedSlots = useMemo(
    () => (currentPage.slots || []).filter((s) => selectedSlotIds.has(s.id)),
    [currentPage, selectedSlotIds],
  );
  const multiSelected = selectedSlotIds.size > 1;

  const handleGroupMoveStart = useCallback(() => {
    groupMoveOriginsRef.current = (pagesRef.current[pageIdx]?.slots || [])
      .filter((s) => selectedSlotIdsRef.current.has(s.id))
      .map((s) => ({ ...s }));
  }, [pageIdx]);

  const handleGroupMove = useCallback(
    (dx, dy) => {
      const origins = groupMoveOriginsRef.current;
      if (!origins?.length) return;
      const byId = new Map(origins.map((o) => [o.id, o]));
      updateCurrentPageSlots((slots) =>
        slots.map((s) => {
          const o = byId.get(s.id);
          if (!o) return s;
          // Sin snap durante el arrastre: el grupo se mueve en bloque
          return clampSlot({
            ...s,
            x: o.x + dx,
            y: o.y + dy,
          });
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageIdx],
  );

  const handleGroupMoveEnd = useCallback(() => {
    const ids = selectedSlotIdsRef.current;
    if (!ids?.size) return;
    updateCurrentPageSlots((slots) =>
      slots.map((s) => (ids.has(s.id) ? snapSlot(clampSlot(s)) : s)),
    );
    groupMoveOriginsRef.current = null;
  }, [pageIdx]);

  const handleGroupScale = useCallback((scaledSlots) => {
    const byId = new Map(scaledSlots.map((s) => [s.id, s]));
    updateCurrentPageSlots((slots) => slots.map((s) => byId.get(s.id) || s));
  }, []);

  const startCanvasMarquee = (e) => {
    if (markTool && markTool !== "select") return;
    if (e.button != null && e.button !== 0) return;
    if (e.target !== e.currentTarget) return;
    e.preventDefault();
    // Clic en vacío del lienzo: quitar selección de marca
    setSelectedMarkId(null);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;
    // Illustrator: solo Shift mantiene/amplía selección con marquee
    const additive = Boolean(e.shiftKey);
    const x0 = (e.clientX - rect.left) / rect.width;
    const y0 = (e.clientY - rect.top) / rect.height;
    setMarquee({ x0, y0, x1: x0, y1: y0, additive });
    if (!additive) clearSlotSelection();
    const onMove = (ev) => {
      const x1 = (ev.clientX - rect.left) / rect.width;
      const y1 = (ev.clientY - rect.top) / rect.height;
      setMarquee((m) => (m ? { ...m, x1, y1 } : null));
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      const x1 = (ev.clientX - rect.left) / rect.width;
      const y1 = (ev.clientY - rect.top) / rect.height;
      const box = {
        x: Math.min(x0, x1),
        y: Math.min(y0, y1),
        w: Math.abs(x1 - x0),
        h: Math.abs(y1 - y0),
      };
      setMarquee(null);
      if (box.w < 0.01 && box.h < 0.01) {
        if (!additive) clearSlotSelection();
        return;
      }
      const hits = (pagesRef.current[pageIdx]?.slots || [])
        .filter((s) =>
          rectsIntersect(box, {
            x: s.x,
            y: s.y,
            w: s.w,
            h: s.h,
          }),
        )
        .map((s) => s.id);
      if (!hits.length) {
        if (!additive) clearSlotSelection();
        return;
      }
      setSelectedSlotIds((prev) => {
        const next = additive ? new Set(prev) : new Set();
        hits.forEach((id) => next.add(id));
        setActiveSlotId(hits[hits.length - 1]);
        return next;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  useEffect(() => {
    pagesRef.current = pages;
  }, [pages]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        e.target?.isContentEditable
      ) {
        return;
      }
      if (e.key === "Escape") {
        if (textDraft) {
          setTextDraft(null);
          return;
        }
        if (draftMark) {
          setDraftMark(null);
          return;
        }
        if (selectedMarkId) {
          setSelectedMarkId(null);
          return;
        }
        if (markTool) {
          setMarkTool(null);
          return;
        }
        clearSlotSelection();
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedMarkId && (!markTool || markTool === "select")) {
          e.preventDefault();
          const sid = selectedMarkId;
          updateCurrentPageMarks((marks) =>
            marks.filter((m) => String(m.id) !== String(sid)),
          );
          setSelectedMarkId(null);
          return;
        }
        if (selectedSlotIdsRef.current.size > 0 && !markTool) {
          e.preventDefault();
          const ids = [...selectedSlotIdsRef.current];
          if ((pagesRef.current[pageIdx]?.slots || []).length <= 1) return;
          setSlotRemoveId(ids[0]);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    clearSlotSelection,
    pageIdx,
    markTool,
    draftMark,
    textDraft,
    selectedMarkId,
  ]);

  useEffect(() => {
    metaRef.current = { titulo, categoryLabel, studioLine, chromeStyle };
  }, [titulo, categoryLabel, studioLine, chromeStyle]);

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);

  const handleStaleConflict = useCallback(async () => {
    setStaleConflict(true);
    setSaveState("error");
    setError(t("skillAgent.moodboard.staleConflictReloadMsg"));
    try {
      const data = await getSkillAgentMoodboardById(moodboardId);
      applyBoard(data);
      setSaveState("saved");
      setStaleConflict(false);
      setError("");
      setOkMsg(t("skillAgent.moodboard.ok.reloadedNewer"));
    } catch {
      /* keep error */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, t]);

  const persistPages = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const run = async () => {
      const payload = pagesRef.current.map((p) => normalizeFreePage(p));
      setSaveState("saving");
      // Evita que el eco del socket de ESTE guardado dispare el banner.
      ignoreOwnSocketUntilRef.current = Date.now() + 4000;
      try {
        const data = await saveSkillAgentMoodboardPages(moodboardId, payload, {
          expectedUpdatedAt: expectedUpdatedAtRef.current,
        });
        applyBoard(data);
        setStaleConflict(false);
        setError("");
        setSaveState("saved");
        ignoreOwnSocketUntilRef.current = Date.now() + 2500;
        return true;
      } catch (err) {
        if (err?.response?.status === 409) {
          await handleStaleConflict();
          return false;
        }
        setSaveState("error");
        setError(
          err?.response?.data?.error ||
            t("skillAgent.moodboard.error.saveLayout"),
        );
        return false;
      }
    };
    const p = saveChainRef.current.then(run, run);
    saveChainRef.current = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- applyBoard es local estable en práctica
  }, [moodboardId, exporting, handleStaleConflict, t]);

  const persistMeta = useCallback(async () => {
    if (!moodboardId || exporting || skipAutosaveRef.current) return false;
    const run = async () => {
      const m = metaRef.current;
      setSaveState("saving");
      ignoreOwnSocketUntilRef.current = Date.now() + 4000;
      try {
        const data = await updateSkillAgentMoodboard(moodboardId, {
          Titulo: m.titulo,
          CategoryLabel: m.categoryLabel,
          StudioLine: m.studioLine,
          ChromeStyleJson: m.chromeStyle,
          ExpectedUpdatedAt: expectedUpdatedAtRef.current,
        });
        applyBoard(data);
        setStaleConflict(false);
        setError("");
        setSaveState("saved");
        ignoreOwnSocketUntilRef.current = Date.now() + 2500;
        return true;
      } catch (err) {
        if (err?.response?.status === 409) {
          await handleStaleConflict();
          return false;
        }
        setSaveState("error");
        setError(
          err?.response?.data?.error ||
            t("skillAgent.moodboard.error.saveData"),
        );
        return false;
      }
    };
    const p = saveChainRef.current.then(run, run);
    saveChainRef.current = p.then(
      () => undefined,
      () => undefined,
    );
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, exporting, handleStaleConflict, t]);

  const flushAutosave = useCallback(async () => {
    if (pagesTimerRef.current) {
      clearTimeout(pagesTimerRef.current);
      pagesTimerRef.current = null;
    }
    if (metaTimerRef.current) {
      clearTimeout(metaTimerRef.current);
      metaTimerRef.current = null;
    }
    const okPages = await persistPages();
    const okMeta = await persistMeta();
    return okPages && okMeta;
  }, [persistPages, persistMeta]);

  // Presencia + soft refresh si otro guarda
  useEffect(() => {
    if (!moodboardId) return undefined;
    const id = Number(moodboardId);
    if (!Number.isFinite(id) || id <= 0) return undefined;
    socket.emit("join-moodboard", {
      moodboardId: id,
      userId: viewerUserId,
      username: viewerName || t("skillAgent.moodboard.peerFallbackName"),
    });
    const onPresence = (payload) => {
      if (Number(payload?.moodboardId) !== id) return;
      const viewers = Array.isArray(payload?.viewers) ? payload.viewers : [];
      setPeerViewers(
        viewers.filter((v) => {
          if (viewerUserId == null) return true;
          return Number(v.userId) !== Number(viewerUserId);
        }),
      );
    };
    const onUpdated = async (payload) => {
      if (Number(payload?.moodboardId) !== id) return;
      // Nuestro propio autosave también emite: no es conflicto.
      if (
        viewerUserId != null &&
        payload?.userId != null &&
        Number(payload.userId) === Number(viewerUserId)
      ) {
        return;
      }
      if (Date.now() < ignoreOwnSocketUntilRef.current) return;
      if (softRefreshBusyRef.current) return;
      if (
        saveStateRef.current === "unsaved" ||
        saveStateRef.current === "saving"
      ) {
        setStaleConflict(true);
        setError(t("skillAgent.moodboard.staleConflictBanner"));
        return;
      }
      softRefreshBusyRef.current = true;
      try {
        const data = await getSkillAgentMoodboardById(moodboardId);
        applyBoard(data);
        setStaleConflict(false);
        setError("");
      } catch {
        /* ignore */
      } finally {
        softRefreshBusyRef.current = false;
      }
    };
    socket.on("moodboard-presence", onPresence);
    socket.on("moodboard-updated", onUpdated);
    return () => {
      socket.emit("leave-moodboard", { moodboardId: id });
      socket.off("moodboard-presence", onPresence);
      socket.off("moodboard-updated", onUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moodboardId, viewerName, viewerUserId, t]);

  // Autosave layout (debounce)
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    pagesTimerRef.current = setTimeout(() => {
      persistPages();
    }, 750);
    return () => {
      if (pagesTimerRef.current) clearTimeout(pagesTimerRef.current);
    };
  }, [pages, loading, exporting, persistPages]);

  // Autosave título / barras PDF
  useEffect(() => {
    if (loading || skipAutosaveRef.current || exporting) return undefined;
    setSaveState((s) => (s === "saved" ? "unsaved" : s));
    if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    metaTimerRef.current = setTimeout(() => {
      persistMeta();
    }, 900);
    return () => {
      if (metaTimerRef.current) clearTimeout(metaTimerRef.current);
    };
  }, [
    titulo,
    categoryLabel,
    studioLine,
    chromeStyle,
    loading,
    exporting,
    persistMeta,
  ]);

  // Aviso si cierran la pestaña con cambios pendientes
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (saveState === "unsaved" || saveState === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [saveState]);

  const handleSaveAll = async () => {
    setBusy(true);
    setError("");
    try {
      const ok = await flushAutosave();
      if (ok) {
        onChangedRef.current?.();
        setOkMsg(t("skillAgent.moodboard.ok.saved"));
      }
    } finally {
      setBusy(false);
    }
  };

  const assignAssetToSlot = (slotId, assetId) => {
    updateCurrentPageSlots((slots) =>
      slots.map((s) =>
        s.id === slotId ? { ...s, assetId: Number(assetId) } : s,
      ),
    );
  };

  const placeAssetInFirstEmpty = (assetId) => {
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      for (let i = 0; i < next.length; i += 1) {
        const empty = next[i].slots.find((s) => s.assetId == null);
        if (empty) {
          empty.assetId = Number(assetId);
          setPageIdx(i);
          setSelectedSlotIds(new Set([empty.id]));
          setActiveSlotId(empty.id);
          return next;
        }
      }
      const page = emptyFreePage();
      page.slots[0].assetId = Number(assetId);
      setPageIdx(next.length);
      setSelectedSlotIds(new Set([page.slots[0].id]));
      setActiveSlotId(page.slots[0].id);
      return [...next, page];
    });
  };

  const toggleGallerySelect = (assetId) => {
    const id = Number(assetId);
    if (!Number.isFinite(id)) return;
    setGallerySelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /** Coloca la selección en la página actual (huecos primero; si faltan, nuevas casillas). */
  const placeSelectedOnCurrentPage = () => {
    const ids = [...gallerySelected];
    if (!ids.length) return;
    setPages((prev) => {
      const next = prev.map((p) => normalizeFreePage(p));
      const page = next[pageIdx] || emptyFreePage();
      if (!next[pageIdx]) next.push(page);
      let slots = [...(page.slots || [])];
      let maxZ = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      const queue = [...ids];
      slots = slots.map((s) => {
        if (s.assetId != null || !queue.length) return s;
        return clampSlot({ ...s, assetId: Number(queue.shift()) });
      });
      while (queue.length) {
        maxZ += 1;
        const n = slots.length;
        const col = n % 3;
        const row = Math.floor(n / 3);
        slots.push(
          clampSlot({
            id: newSlotId("p"),
            x: 0.05 + col * 0.32,
            y: 0.08 + row * 0.3,
            w: 0.28,
            h: 0.26,
            shape: "rect",
            z: maxZ,
            assetId: Number(queue.shift()),
          }),
        );
      }
      next[pageIdx] = { slots };
      return next;
    });
    setActiveSlotId(null);
    setSelectedSlotIds(new Set());
    const n = gallerySelected.size;
    setGallerySelected(new Set());
    setOkMsg(
      `${n} foto${n === 1 ? "" : "s"} colocada${n === 1 ? "" : "s"} en página ${pageIdx + 1}.`,
    );
  };

  const handleAddSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    const slot = clampSlot({
      id: newSlotId(),
      x: 0.35,
      y: 0.35,
      w: 0.28,
      h: 0.3,
      shape: "rect",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    selectOnlySlot(slot.id);
  };

  const handleAddCircleSlot = () => {
    const maxZ = Math.max(
      0,
      ...(currentPage.slots || []).map((s) => Number(s.z) || 0),
    );
    // Landscape page ~297/210 → keep visual circle-ish defaults.
    const slot = clampSlot({
      id: newSlotId("c"),
      x: 0.38,
      y: 0.32,
      w: 0.2,
      h: 0.28,
      shape: "circle",
      z: maxZ + 1,
      assetId: null,
    });
    updateCurrentPageSlots((slots) => [...slots, slot]);
    selectOnlySlot(slot.id);
  };

  const handleToggleSlotShape = () => {
    if (!selectedSlotIds.size) return;
    updateCurrentPageSlots((slots) =>
      slots.map((s) => {
        if (!selectedSlotIds.has(s.id)) return s;
        const nextShape = s.shape === "circle" ? "rect" : "circle";
        return clampSlot({ ...s, shape: nextShape });
      }),
    );
  };

  const handleBringToFront = () => {
    if (!selectedSlotIds.size) return;
    updateCurrentPageSlots((slots) => {
      const selected = slots
        .filter((s) => selectedSlotIds.has(s.id))
        .sort((a, b) => (Number(a.z) || 0) - (Number(b.z) || 0));
      let z = Math.max(0, ...slots.map((s) => Number(s.z) || 0));
      const zMap = new Map();
      for (const s of selected) {
        z += 1;
        zMap.set(s.id, z);
      }
      return slots.map((s) =>
        zMap.has(s.id) ? clampSlot({ ...s, z: zMap.get(s.id) }) : s,
      );
    });
  };

  const handleSendToBack = () => {
    if (!selectedSlotIds.size) return;
    updateCurrentPageSlots((slots) => {
      const selected = slots
        .filter((s) => selectedSlotIds.has(s.id))
        .sort((a, b) => (Number(b.z) || 0) - (Number(a.z) || 0));
      let z = Math.min(...slots.map((s) => Number(s.z) || 0));
      const zMap = new Map();
      for (const s of selected) {
        z = Math.max(0, z - 1);
        zMap.set(s.id, z);
      }
      return slots.map((s) =>
        zMap.has(s.id) ? clampSlot({ ...s, z: zMap.get(s.id) }) : s,
      );
    });
  };

  const applyDerivedAssetResponse = (data, fromAssetId) => {
    if (Array.isArray(data?.assets)) setAssets(data.assets);
    if (Array.isArray(data?.PagesJson)) {
      setPages(normalizePages(data.PagesJson));
    } else if (data?.asset?.id) {
      const nextId = Number(data.asset.id);
      const fromId = Number(fromAssetId);
      setPages((prev) =>
        prev.map((p) => ({
          ...normalizeFreePage(p),
          slots: normalizeFreePage(p).slots.map((s) =>
            Number(s.assetId) === fromId
              ? clampSlot({
                  ...s,
                  assetId: nextId,
                  zoom: 1,
                  panX: 0.5,
                  panY: 0.5,
                })
              : s,
          ),
        })),
      );
    }
    if (data?.Titulo != null || data?.id) {
      setBoard((p) => ({ ...p, ...data, assets: undefined }));
    }
  };

  const runRemoveBackground = async () => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    setRemoveBgConfirmOpen(false);
    try {
      const data = await removeBgSkillAgentMoodboardAsset(moodboardId, assetId);
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      if (Array.isArray(data?.PagesJson)) {
        setPages(normalizePages(data.PagesJson));
      } else if (data?.asset?.id) {
        const nextId = Number(data.asset.id);
        const fromId = Number(assetId);
        setPages((prev) =>
          prev.map((p) => ({
            ...normalizeFreePage(p),
            slots: normalizeFreePage(p).slots.map((s) =>
              Number(s.assetId) === fromId
                ? clampSlot({ ...s, assetId: nextId })
                : s,
            ),
          })),
        );
      }
      if (data?.Titulo != null || data?.id) {
        setBoard((p) => ({ ...p, ...data, assets: undefined }));
      }
      setOkMsg(t("skillAgent.moodboard.ok.bgRemovedDetail"));
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          t("skillAgent.moodboard.error.removeBg"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveBackground = () => {
    if (!activeSlot?.assetId || busy) return;
    if (activeAssetIsCutout) {
      setRemoveBgConfirmOpen(true);
      return;
    }
    runRemoveBackground();
  };

  const handleApplyContourStroke = async () => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    setStrokeAnchor(null);
    try {
      const data = await contourStrokeSkillAgentMoodboardAsset(
        moodboardId,
        assetId,
        { width: strokeWidth, color: strokeColor },
      );
      applyDerivedAssetResponse(data, assetId);
      setOkMsg(
        t("skillAgent.moodboard.ok.strokeAppliedContour", {
          n: strokeWidth,
        }),
      );
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          t("skillAgent.moodboard.error.stroke"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmCrop = async (rect) => {
    const assetId = activeSlot?.assetId;
    if (!moodboardId || !assetId || busy) return;
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const data = await cropSkillAgentMoodboardAsset(
        moodboardId,
        assetId,
        rect,
      );
      applyDerivedAssetResponse(data, assetId);
      setCropOpen(false);
      setOkMsg(t("skillAgent.moodboard.ok.croppedDetail"));
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err.message ||
          t("skillAgent.moodboard.error.crop"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveSlot = (slotId) => {
    const ids =
      selectedSlotIds.size > 1 && selectedSlotIds.has(slotId)
        ? [...selectedSlotIds]
        : slotId
          ? [slotId]
          : activeSlotId
            ? [activeSlotId]
            : [];
    if (!ids.length) return;
    updateCurrentPageSlots((slots) => {
      if (slots.length <= 1) return slots;
      const next = slots.filter((s) => !ids.includes(s.id));
      return next.length ? next : slots;
    });
    clearSlotSelection();
    setSlotRemoveId(null);
  };

  const handleConfirmRemovePage = () => {
    if (pages.length <= 1) {
      setPageRemoveOpen(false);
      return;
    }
    const removeAt = pageIdx;
    setPages((prev) =>
      prev.filter((_, i) => i !== removeAt).map(normalizeFreePage),
    );
    setPageIdx((idx) => {
      if (idx > removeAt) return idx - 1;
      if (idx === removeAt) return Math.max(0, Math.min(idx, pages.length - 2));
      return idx;
    });
    clearSlotSelection();
    setPageRemoveOpen(false);
    setOkMsg(t("skillAgent.moodboard.ok.pageDeleted", { n: removeAt + 1 }));
  };

  const handleAviatorPreset = () => {
    setPages((prev) =>
      prev.map((p, i) =>
        i === pageIdx ? applyAviatorPresetToPage(p) : normalizeFreePage(p),
      ),
    );
    clearSlotSelection();
    setAviatorConfirmOpen(false);
    setOkMsg(t("skillAgent.moodboard.ok.aviatorDone", { n: pageIdx + 1 }));
  };

  const handleAutoPages = async () => {
    setBusy(true);
    try {
      const data = await autoSkillAgentMoodboardPages(moodboardId);
      applyBoard(data);
      setPageIdx(0);
      clearSlotSelection();
      setAutoPagesConfirmOpen(false);
      onChanged?.();
      setOkMsg(t("skillAgent.moodboard.ok.aviatorDistributed"));
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.distribute"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleAddFromPool = async (idsOverride) => {
    const rawIds = Array.isArray(idsOverride) ? idsOverride : [...poolPick];
    const ids = rawIds
      .map((id) => Number(id))
      .filter((id) => id && !usedSearchItemIds.has(id));
    if (!ids.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, {
        searchItemIds: ids,
      });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      // Solo sincronizamos layout si el servidor rellenó páginas vacías;
      // si no, las fotos quedan en el board sin colocar (siguiente paso).
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (boardWasEmpty && placedOnServer) {
        skipAutosaveRef.current = true;
        setPages(serverPages);
        setTimeout(() => {
          skipAutosaveRef.current = false;
        }, 80);
      }
      setPoolPick(new Set());
      setAddPhotosOpen(false);
      onChanged?.();
      setOkMsg(
        t("skillAgent.moodboard.ok.assetsAddedPlace", {
          n: added.length || ids.length,
        }),
      );
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.addAssets"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (fileList) => {
    const files = [...(fileList || [])];
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      const data = await addSkillAgentMoodboardAssets(moodboardId, { files });
      const added = Array.isArray(data?.added) ? data.added : [];
      const serverPages = normalizePages(data?.PagesJson);
      const boardWasEmpty = !pagesRef.current.some((p) =>
        (p.slots || []).some((s) => s.assetId != null),
      );
      const placedOnServer = added.every((a) =>
        serverPages.some((p) =>
          p.slots.some((s) => Number(s.assetId) === Number(a.id)),
        ),
      );
      if (Array.isArray(data?.assets)) setAssets(data.assets);
      setBoard(data);
      if (boardWasEmpty && placedOnServer) {
        skipAutosaveRef.current = true;
        setPages(serverPages);
        setTimeout(() => {
          skipAutosaveRef.current = false;
        }, 80);
      }
      onChanged?.();
      setOkMsg(t("skillAgent.moodboard.ok.uploadedPlace", { n: files.length }));
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.uploadShort"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveAsset = async (assetId) => {
    if (!assetId) return;
    setBusy(true);
    try {
      const data = await deleteSkillAgentMoodboardAsset(moodboardId, assetId);
      applyBoard(data);
      setAssetRemoveId(null);
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.moodboard.error.removeShort"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    if (exporting || busy) return;
    setExportConfirmOpen(false);
    setExporting(true);
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      await flushAutosave();
      const data = await exportSkillAgentMoodboardPdf(moodboardId, {
        customerFolderName: board?.CustomerName || undefined,
      });
      applyBoard(data.moodboard || data.search || data);

      if (data.pdfBase64) {
        try {
          const bin = atob(data.pdfBase64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = data.pdfFileName || "moodboard.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch {
          /* download optional */
        }
      }

      onChanged?.();
      if (data.drivePdfFileUrl) {
        setOkMsg(t("skillAgent.moodboard.ok.pdfExportedDrive"));
      } else if (data.driveNote) {
        setOkMsg(
          t("skillAgent.moodboard.ok.pdfDriveNote", { note: data.driveNote }),
        );
      } else {
        setOkMsg(t("skillAgent.moodboard.ok.pdfDownloaded"));
      }
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.exportPdf"),
      );
    } finally {
      setExporting(false);
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteSkillAgentMoodboard(moodboardId);
      setDeleteOpen(false);
      onDeleted?.(moodboardId);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.moodboard.error.delete"),
      );
      setBusy(false);
    }
  };

  const catParts = splitCategoryLabel(categoryLabel);
  const studioParts = splitStudioLine(studioLine);

  if (loading && !board) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!board) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Moodboard no encontrado.
      </Alert>
    );
  }

  const startChromeBarDrag = (which, e) => {
    e.preventDefault();
    e.stopPropagation();
    clearSlotSelection();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const elRect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - elRect.left;
    const offsetY = e.clientY - elRect.top;
    const onMove = (ev) => {
      if (rect.width < 1 || rect.height < 1) return;
      const x = Math.min(
        0.95,
        Math.max(0, (ev.clientX - offsetX - rect.left) / rect.width),
      );
      const y = Math.min(
        0.95,
        Math.max(0, (ev.clientY - offsetY - rect.top) / rect.height),
      );
      setChromeStyle((prev) => ({
        ...prev,
        [which]: { ...prev[which], x, y },
      }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const titleBarPos =
    chromeStyle.title.x != null && chromeStyle.title.y != null
      ? {
          left: `${chromeStyle.title.x * 100}%`,
          top: `${chromeStyle.title.y * 100}%`,
          right: "auto",
          bottom: "auto",
        }
      : { top: 0, right: 0 };
  const studioBarPos =
    chromeStyle.studio.x != null && chromeStyle.studio.y != null
      ? {
          left: `${chromeStyle.studio.x * 100}%`,
          top: `${chromeStyle.studio.y * 100}%`,
          right: "auto",
          bottom: "auto",
        }
      : { bottom: 0, left: 0 };

  return (
    <Stack
      spacing={0}
      sx={{
        flex: 1,
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Backdrop
        open={exporting}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          bgcolor: alpha("#0f172a", 0.55),
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <CircularProgress size={48} sx={{ color: "#fff" }} />
        <Typography fontWeight={800} sx={{ color: "#fff", fontSize: 18 }}>
          {t("skillAgent.moodboard.exportBackdropTitle")}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
          {t("skillAgent.moodboard.exportBackdropBody")}
        </Typography>
      </Backdrop>

      {peerViewers.length || staleConflict ? (
        <Alert
          severity={staleConflict ? "warning" : "info"}
          sx={{
            flexShrink: 0,
            borderRadius: 0,
            py: 0.25,
            "& .MuiAlert-message": { width: "100%" },
          }}
          action={
            staleConflict ? (
              <Button
                color="inherit"
                size="small"
                sx={{ fontWeight: 800, textTransform: "none" }}
                onClick={() => handleStaleConflict()}
              >
                {t("skillAgent.common.reload")}
              </Button>
            ) : null
          }
        >
          {staleConflict ? t("skillAgent.moodboard.staleConflict") : null}
          {peerViewers.length
            ? `${staleConflict ? " · " : ""}${t(
                peerViewers.length === 1
                  ? "skillAgent.moodboard.peerViewersOne"
                  : "skillAgent.moodboard.peerViewersMany",
                {
                  names: peerViewers
                    .map(
                      (v) =>
                        v.username ||
                        t("skillAgent.moodboard.peerFallbackName"),
                    )
                    .join(", "),
                },
              )}`
            : null}
        </Alert>
      ) : null}

      {/* Barras fijas: cabecera + páginas/casillas (fuera del scroll) */}
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 40,
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.98),
          borderBottom: "1px solid",
          borderColor: "divider",
          boxShadow: "0 6px 16px rgba(15,23,42,0.06)",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1.25}
          useFlexGap
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 1,
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              flex: "0 0 auto",
              minWidth: "max-content",
              maxWidth: "none",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: "nowrap" }}
            >
              <Typography
                component="div"
                noWrap
                sx={{
                  fontWeight: 800,
                  fontSize: "1.25rem",
                  lineHeight: 1.25,
                  letterSpacing: "-0.01em",
                  flexShrink: 0,
                }}
              >
                {titulo || board.Titulo}
              </Typography>
              <Chip
                size="small"
                label={moodboardStatusLabel(board.Status)}
                color={board.Status === "ready" ? "success" : "default"}
                sx={{ height: 22, fontWeight: 700, flexShrink: 0 }}
              />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ flexShrink: 0 }}
            >
              {[board.CustomerName, board.Temporada]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            sx={{
              flex: "0 1 auto",
              justifyContent: "flex-end",
              minWidth: 0,
              overflow: "auto",
              flexWrap: "nowrap",
            }}
          >
            <ToolGroup label={t("skillAgent.moodboard.toolGroup.board")}>
              {typeof onOpenHelp === "function" ? (
                <Tooltip title={t("skillAgent.moodboard.help")}>
                  <IconButton
                    size="small"
                    onClick={onOpenHelp}
                    aria-label={t("skillAgent.moodboard.help")}
                  >
                    <HelpOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip
                title={
                  exporting
                    ? t("skillAgent.moodboard.exporting")
                    : t("skillAgent.moodboard.exportPdf")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={busy || exporting || !assets.length}
                    onClick={() => setExportConfirmOpen(true)}
                    aria-label={t("skillAgent.moodboard.exportPdf")}
                  >
                    {exporting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <PictureAsPdfOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {board.DrivePdfFileUrl ? (
                <Tooltip title={t("skillAgent.moodboard.openPdfDrive")}>
                  <IconButton
                    size="small"
                    href={board.DrivePdfFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    component="a"
                  >
                    <OpenInNewOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title={t("skillAgent.moodboard.pdfMeta")}>
                <IconButton
                  size="small"
                  onClick={() => setMetaOpen(true)}
                  aria-label={t("skillAgent.moodboard.pdfMeta")}
                >
                  <TextFieldsOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.moreOptions")}>
                <IconButton
                  size="small"
                  onClick={(e) => setLayoutMenuEl(e.currentTarget)}
                  aria-label={t("skillAgent.moodboard.moreOptions")}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.pageZoomOut")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={viewZoom <= PAGE_VIEW_ZOOM_MIN}
                    onClick={() =>
                      setViewZoom((z) =>
                        clampPageViewZoom(z - PAGE_VIEW_ZOOM_STEP),
                      )
                    }
                    aria-label={t("skillAgent.moodboard.pageZoomOut")}
                  >
                    <ZoomOutOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.pageZoomReset")}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setViewZoom(1)}
                  sx={{
                    minWidth: 44,
                    px: 0.5,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    lineHeight: 1,
                  }}
                  aria-label={t("skillAgent.moodboard.pageZoomPct", {
                    pct: Math.round(viewZoom * 100),
                  })}
                >
                  {Math.round(viewZoom * 100)}%
                </Button>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.pageZoomIn")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={viewZoom >= PAGE_VIEW_ZOOM_MAX}
                    onClick={() =>
                      setViewZoom((z) =>
                        clampPageViewZoom(z + PAGE_VIEW_ZOOM_STEP),
                      )
                    }
                    aria-label={t("skillAgent.moodboard.pageZoomIn")}
                  >
                    <ZoomInOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={layoutMenuEl}
                open={Boolean(layoutMenuEl)}
                onClose={() => setLayoutMenuEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  disabled={busy}
                  onClick={() => {
                    setLayoutMenuEl(null);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: "error.main" }}
                >
                  {t("skillAgent.moodboard.deleteMenu")}
                </MenuItem>
              </Menu>
            </ToolGroup>

            <ToolGroup
              label={t("skillAgent.moodboard.toolGroup.photos")}
              accent
            >
              <Tooltip title={t("skillAgent.moodboard.addFromSearch")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setPoolPick(new Set());
                      setAddPhotosOpen(true);
                    }}
                    aria-label={t("skillAgent.moodboard.addPhotosAria")}
                    sx={{ color: "warning.main" }}
                  >
                    <PhotoLibraryOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={t("skillAgent.moodboard.boardPhotos", {
                  n: assets.length,
                })}
              >
                <span>
                  <IconButton
                    size="small"
                    color={assets.length ? "primary" : "default"}
                    disabled={busy}
                    onClick={() => setBoardPhotosOpen(true)}
                    aria-label={t("skillAgent.moodboard.boardPhotosAria")}
                  >
                    <CollectionsOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.uploadPc")}>
                <span>
                  <IconButton
                    size="small"
                    component="label"
                    disabled={busy}
                    aria-label={t("skillAgent.moodboard.uploadPc")}
                  >
                    <CloudUploadOutlinedIcon fontSize="small" />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        handleUpload(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </ToolGroup>
          </Stack>
        </Stack>
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            pb: 1,
            pt: 0.25,
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              gap: 0.5,
              flexWrap: "nowrap",
              overflow: "auto",
              pb: 0.25,
            }}
          >
            <ToolGroup grow label={t("skillAgent.moodboard.toolGroup.pages")}>
              <Tabs
                value={pageIdx}
                onChange={(_, v) => {
                  setPageIdx(v);
                  clearSlotSelection();
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 32,
                  flex: 1,
                  minWidth: 0,
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-flexContainer": { gap: 0.25 },
                  "& .MuiTabScrollButton-root": {
                    width: 28,
                    "&.Mui-disabled": { opacity: 0.25 },
                  },
                  "& .MuiTab-root": {
                    minHeight: 28,
                    minWidth: 0,
                    py: 0.25,
                    px: 1,
                    my: 0.25,
                    borderRadius: 1.25,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textTransform: "none",
                    color: "text.secondary",
                    "&.Mui-selected": {
                      color: "primary.main",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                    },
                    "&:hover": {
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
                    },
                  },
                }}
              >
                {pages.map((_, i) => (
                  <Tab
                    key={i}
                    label={`${i + 1}`}
                    title={t("skillAgent.moodboard.pageTabTitle", { n: i + 1 })}
                  />
                ))}
              </Tabs>
            </ToolGroup>

            <ToolGroup label={t("skillAgent.moodboard.toolGroup.pages")}>
              <Tooltip title={t("skillAgent.moodboard.addPage")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setPages((p) => [
                      ...p.map(normalizeFreePage),
                      emptyFreePage(),
                    ]);
                    setPageIdx(pages.length);
                    clearSlotSelection();
                  }}
                >
                  <AddOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  pages.length <= 1
                    ? t("skillAgent.moodboard.cannotDeleteLastPage")
                    : t("skillAgent.moodboard.deletePageTooltip", {
                        n: pageIdx + 1,
                      })
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={pages.length <= 1}
                    onClick={() => setPageRemoveOpen(true)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.aviatorTemplate")}>
                <IconButton
                  size="small"
                  onClick={() => setAviatorConfirmOpen(true)}
                >
                  <ViewQuiltOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.autoPagesTooltip")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || !assets.length}
                    onClick={() => setAutoPagesConfirmOpen(true)}
                  >
                    <GridViewOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </ToolGroup>

            <ToolGroup label={t("skillAgent.moodboard.toolGroup.slots")} accent>
              <Tooltip title={t("skillAgent.moodboard.addSlotRect")}>
                <IconButton size="small" onClick={handleAddSlot}>
                  <CropFreeOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.addSlotCircle")}>
                <IconButton size="small" onClick={handleAddCircleSlot}>
                  <CircleOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  multiSelected
                    ? t("skillAgent.moodboard.toggleShapeGroup")
                    : activeSlot?.shape === "circle"
                      ? t("skillAgent.moodboard.toggleToSquare")
                      : t("skillAgent.moodboard.toggleToCircle")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!selectedSlotIds.size}
                    onClick={handleToggleSlotShape}
                  >
                    {activeSlot?.shape === "circle" ? (
                      <ShapeToSquareIcon />
                    ) : (
                      <ShapeToCircleIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  multiSelected
                    ? t("skillAgent.moodboard.bringFrontGroup", {
                        n: selectedSlotIds.size,
                      })
                    : t("skillAgent.moodboard.bringFront")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!selectedSlotIds.size}
                    onClick={handleBringToFront}
                  >
                    <FlipToFrontOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  multiSelected
                    ? t("skillAgent.moodboard.sendBackGroup", {
                        n: selectedSlotIds.size,
                      })
                    : t("skillAgent.moodboard.sendBack")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!selectedSlotIds.size}
                    onClick={handleSendToBack}
                  >
                    <FlipToBackOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  activeAssetIsCutout
                    ? t("skillAgent.moodboard.removeBgCutout")
                    : t("skillAgent.moodboard.removeBg")
                }
              >
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={busy || !activeSlot?.assetId}
                    onClick={handleRemoveBackground}
                  >
                    <AutoFixHighOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.stroke")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || !activeSlot?.assetId}
                    onClick={(e) => setStrokeAnchor(e.currentTarget)}
                  >
                    <BorderColorOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.crop")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={busy || !activeSlot?.assetId}
                    onClick={() => setCropOpen(true)}
                  >
                    <CropOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </ToolGroup>

            <ToolGroup
              label={t("skillAgent.moodboard.toolGroup.marks")}
              accent={Boolean(markTool) && markTool !== "select"}
            >
              <Tooltip title={t("skillAgent.moodboard.markSelect")}>
                <IconButton
                  size="small"
                  color={
                    !markTool || markTool === "select" ? "primary" : "default"
                  }
                  onClick={() => {
                    // Modo normal: casillas + mover/editar marcas (sin bloquear el lienzo)
                    setMarkTool(null);
                    setDraftMark(null);
                    setTextDraft(null);
                  }}
                >
                  <NearMeOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markPen")}>
                <IconButton
                  size="small"
                  color={markTool === "pen" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("pen");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <GestureOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markLine")}>
                <IconButton
                  size="small"
                  color={markTool === "line" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("line");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <HorizontalRuleOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markArrow")}>
                <IconButton
                  size="small"
                  color={markTool === "arrow" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("arrow");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <CallMadeOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markEllipse")}>
                <IconButton
                  size="small"
                  color={markTool === "ellipse" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("ellipse");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <RadioButtonUncheckedOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markRect")}>
                <IconButton
                  size="small"
                  color={markTool === "rect" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("rect");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <CropSquareOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markText")}>
                <IconButton
                  size="small"
                  color={markTool === "text" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("text");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <TitleOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markEraser")}>
                <IconButton
                  size="small"
                  color={markTool === "eraser" ? "primary" : "default"}
                  onClick={() => {
                    setMarkTool("eraser");
                    setSelectedMarkId(null);
                    clearSlotSelection();
                  }}
                >
                  <AutoFixOffOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("skillAgent.moodboard.markColor")}>
                <Box
                  component="input"
                  type="color"
                  value={markColor}
                  onChange={(e) => {
                    const next = e.target.value;
                    setMarkColor(next);
                    patchSelectedMark({ color: next });
                  }}
                  sx={{
                    width: 26,
                    height: 26,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 0,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
              {markTool === "text" ? (
                <>
                  <Tooltip title={t("skillAgent.moodboard.markFontFamily")}>
                    <Box
                      component="select"
                      value={markFontFamily}
                      onChange={(e) => {
                        const next = normalizeMarkFontFamily(e.target.value);
                        setMarkFontFamily(next);
                        patchSelectedMark({ fontFamily: next });
                        setTextDraft((prev) =>
                          prev ? { ...prev, fontFamily: next } : prev,
                        );
                      }}
                      aria-label={t("skillAgent.moodboard.markFontFamily")}
                      sx={{
                        maxWidth: 92,
                        height: 26,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 0.5,
                        bgcolor: "background.paper",
                        cursor: "pointer",
                        fontFamily: markFontCss(markFontFamily),
                      }}
                    >
                      {Object.keys(MARK_FONT_FAMILIES).map((id) => (
                        <option key={id} value={id}>
                          {t(`skillAgent.moodboard.fontFamily.${id}`)}
                        </option>
                      ))}
                    </Box>
                  </Tooltip>
                  <Tooltip title={t("skillAgent.moodboard.markFontSize")}>
                    <Box
                      component="select"
                      value={markFontSize}
                      onChange={(e) => {
                        const next = Math.min(
                          72,
                          Math.max(8, Number(e.target.value) || 14),
                        );
                        setMarkFontSize(next);
                        patchSelectedMark({ fontSize: next });
                        setTextDraft((prev) =>
                          prev ? { ...prev, fontSize: next } : prev,
                        );
                      }}
                      aria-label={t("skillAgent.moodboard.markFontSize")}
                      sx={{
                        width: 52,
                        height: 26,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 0.5,
                        bgcolor: "background.paper",
                        cursor: "pointer",
                      }}
                    >
                      {[10, 12, 14, 18, 24, 32, 48].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Box>
                  </Tooltip>
                </>
              ) : null}
              <Tooltip
                title={t("skillAgent.moodboard.markStroke", { n: markStroke })}
              >
                <Box
                  sx={{
                    width: 56,
                    px: 0.5,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Slider
                    size="small"
                    min={1}
                    max={8}
                    step={0.5}
                    value={markStroke}
                    onChange={(_, v) => {
                      const next = Number(v);
                      setMarkStroke(next);
                      patchSelectedMark({ stroke: next });
                    }}
                    aria-label={t("skillAgent.moodboard.markStrokeAria")}
                  />
                </Box>
              </Tooltip>
            </ToolGroup>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            <Tooltip
              title={
                saveState === "error"
                  ? t("skillAgent.moodboard.saveTooltip.error")
                  : saveState === "unsaved"
                    ? t("skillAgent.moodboard.saveTooltip.unsaved")
                    : saveState === "saving"
                      ? t("skillAgent.moodboard.saveTooltip.saving")
                      : t("skillAgent.moodboard.saveTooltip.saved")
              }
            >
              <Button
                size="small"
                variant="text"
                disabled={busy || saveState === "saving"}
                onClick={handleSaveAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  flexShrink: 0,
                  minWidth: 0,
                  px: 1,
                  color:
                    saveState === "error"
                      ? "error.main"
                      : saveState === "unsaved"
                        ? "warning.dark"
                        : "text.secondary",
                }}
              >
                {saveState === "saving"
                  ? t("skillAgent.moodboard.saveState.saving")
                  : saveState === "unsaved"
                    ? t("skillAgent.moodboard.saveState.unsaved")
                    : saveState === "error"
                      ? t("skillAgent.moodboard.saveState.error")
                      : t("skillAgent.moodboard.saveState.saved")}
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      </Box>

      <Box
        ref={viewScrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          p: { xs: 1.25, md: 1.75 },
          pt: 1.25,
        }}
      >
        {error ? (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        ) : null}
        {okMsg ? (
          <Alert
            severity="success"
            onClose={() => setOkMsg("")}
            sx={{ mb: 1.5 }}
          >
            {okMsg}
          </Alert>
        ) : null}

        <Box>
          {/* Canvas — viewZoom solo afecta a la vista, no al PDF */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                position: "relative",
                bgcolor: "#f3f3f3",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                p: 0,
                overflow: "hidden",
                width: `${Math.round(viewZoom * 100)}%`,
                maxWidth: "100%",
                aspectRatio: "297 / 210",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                flexShrink: 0,
              }}
            >
              <Box
                ref={canvasRef}
                onPointerDown={startCanvasMarquee}
                onClick={(e) => {
                  if (e.target === e.currentTarget) clearSlotSelection();
                }}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  bgcolor: "#f3f3f3",
                  containerType: "size",
                  containerName: "moodboard",
                }}
              >
                {layeredSlots.map((slot) => (
                  <FreeformSlotBox
                    key={slot.id}
                    slot={slot}
                    selected={selectedSlotIds.has(slot.id)}
                    multiSelected={multiSelected}
                    hideChrome={multiSelected}
                    canvasRef={canvasRef}
                    canRemove={currentPage.slots.length > 1}
                    onSelect={handleSelectSlot}
                    onGroupMoveStart={handleGroupMoveStart}
                    onGroupMove={handleGroupMove}
                    onGroupMoveEnd={handleGroupMoveEnd}
                    onRequestRemove={(id) => setSlotRemoveId(id)}
                    onChange={(next) => {
                      updateCurrentPageSlots((slots) =>
                        slots.map((s) => (s.id === next.id ? next : s)),
                      );
                    }}
                  />
                ))}
                {multiSelected ? (
                  <GroupTransformBox
                    slots={selectedSlots}
                    canvasRef={canvasRef}
                    onScale={handleGroupScale}
                  />
                ) : null}
                {marquee ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${Math.min(marquee.x0, marquee.x1) * 100}%`,
                      top: `${Math.min(marquee.y0, marquee.y1) * 100}%`,
                      width: `${Math.abs(marquee.x1 - marquee.x0) * 100}%`,
                      height: `${Math.abs(marquee.y1 - marquee.y0) * 100}%`,
                      border: "1px solid",
                      borderColor: "primary.main",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                      pointerEvents: "none",
                      zIndex: 1300,
                    }}
                  />
                ) : null}

                <MarksOverlay
                  marks={currentPage.marks || []}
                  tool={markTool}
                  color={markColor}
                  stroke={markStroke}
                  draftMark={draftMark}
                  textDraft={textDraft}
                  markFontSize={markFontSize}
                  markFontFamily={markFontFamily}
                  selectedMarkId={selectedMarkId}
                  onSelectMark={handleSelectMark}
                  onMoveMark={handleMoveMark}
                  onPointerNorm={handleDraftMark}
                  onCommitMark={handleCommitDraftMark}
                  onEraseAt={handleEraseAt}
                  onEraseAlong={handleEraseAlong}
                  onStartText={(x, y) =>
                    setTextDraft({
                      x,
                      y,
                      value: "",
                      fontSize: markFontSize,
                      fontFamily: markFontFamily,
                    })
                  }
                  onEditText={(mark) => {
                    if (!mark || mark.type !== "text") return;
                    const fs = Number(mark.fontSize) || 14;
                    const ff = normalizeMarkFontFamily(mark.fontFamily);
                    setMarkFontSize(fs);
                    setMarkFontFamily(ff);
                    setTextDraft({
                      id: mark.id,
                      x: mark.x,
                      y: mark.y,
                      value: String(mark.text || ""),
                      fontSize: fs,
                      fontFamily: ff,
                    });
                  }}
                  onChangeTextDraft={(value) =>
                    setTextDraft((prev) => (prev ? { ...prev, value } : prev))
                  }
                  onCommitText={() => {
                    const draft = textDraft;
                    setTextDraft(null);
                    if (!draft || !String(draft.value || "").trim()) {
                      // vaciar al editar = borrar la marca
                      if (draft?.id) {
                        updateCurrentPageMarks((marks) =>
                          marks.filter(
                            (m) => String(m.id) !== String(draft.id),
                          ),
                        );
                      }
                      return;
                    }
                    const payload = {
                      type: "text",
                      x: draft.x,
                      y: draft.y,
                      text: String(draft.value).trim(),
                      color: markColor,
                      stroke: markStroke,
                      fontSize: Number(draft.fontSize) || markFontSize || 14,
                      fontFamily: normalizeMarkFontFamily(
                        draft.fontFamily || markFontFamily,
                      ),
                    };
                    if (draft.id) {
                      updateCurrentPageMarks((marks) =>
                        marks.map((m) =>
                          String(m.id) === String(draft.id)
                            ? clampMark({ ...m, ...payload, id: m.id })
                            : m,
                        ),
                      );
                    } else {
                      updateCurrentPageMarks((marks) => [
                        ...marks,
                        clampMark({ ...payload, id: newMarkId() }),
                      ]);
                    }
                  }}
                  onCancelText={() => setTextDraft(null)}
                />

                <Box
                  onPointerDown={(e) => startChromeBarDrag("title", e)}
                  title={t("skillAgent.moodboard.dragTitleBar")}
                  sx={{
                    position: "absolute",
                    ...titleBarPos,
                    bgcolor: chromeStyle.title.bg,
                    color: chromeStyle.title.text,
                    // Escala con el lienzo (cqw), no con el viewport — así no “salta” al abrir el árbol
                    pl: `${1.7 * chromeStyle.title.barScale}cqw`,
                    pr: `${1.55 * chromeStyle.title.barScale}cqw`,
                    py: `${0.65 * chromeStyle.title.barScale}cqw`,
                    zIndex: 8,
                    pointerEvents: "auto",
                    cursor: "grab",
                    userSelect: "none",
                    touchAction: "none",
                    width: `${Math.min(
                      52,
                      36 *
                        Math.min(
                          1.35,
                          Math.max(
                            0.7,
                            0.85 + 0.15 * chromeStyle.title.barScale,
                          ),
                        ),
                    )}%`,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "flex-end",
                    gap: "0.35cqw",
                    flexWrap: "nowrap",
                    clipPath: chromeBarClipPath(
                      chromeStyle.title.shape,
                      "title",
                      `${2.1 * chromeStyle.title.barScale}cqw`,
                    ),
                    WebkitClipPath: chromeBarClipPath(
                      chromeStyle.title.shape,
                      "title",
                      `${2.1 * chromeStyle.title.barScale}cqw`,
                    ),
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      fontWeight: 800,
                      fontSize: `${1.54 * chromeStyle.title.fontScale}cqw`,
                      lineHeight: 1.2,
                      color: chromeStyle.title.text,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {catParts.main ||
                      t("skillAgent.moodboard.canvasTitleFallback")}
                  </Typography>
                  {catParts.sub ? (
                    <Typography
                      component="span"
                      noWrap
                      sx={{
                        fontWeight: 400,
                        fontSize: `${1.31 * chromeStyle.title.fontScale}cqw`,
                        lineHeight: 1.2,
                        color: chromeStyle.title.subText,
                        whiteSpace: "nowrap",
                      }}
                    >
                      - {catParts.sub}
                    </Typography>
                  ) : null}
                </Box>

                <Box
                  onPointerDown={(e) => startChromeBarDrag("studio", e)}
                  title={t("skillAgent.moodboard.dragStudioBar")}
                  sx={{
                    position: "absolute",
                    ...studioBarPos,
                    bgcolor: chromeStyle.studio.bg,
                    color: chromeStyle.studio.contact,
                    pl: `${1.4 * chromeStyle.studio.barScale}cqw`,
                    pr: `${1.9 * chromeStyle.studio.barScale}cqw`,
                    py: `${0.75 * chromeStyle.studio.barScale}cqw`,
                    zIndex: 8,
                    pointerEvents: "auto",
                    cursor: "grab",
                    userSelect: "none",
                    touchAction: "none",
                    width: `${Math.min(
                      55,
                      40 *
                        Math.min(
                          1.35,
                          Math.max(
                            0.7,
                            0.85 + 0.15 * chromeStyle.studio.barScale,
                          ),
                        ),
                    )}%`,
                    maxWidth: "100%",
                    overflow: "hidden",
                    clipPath: chromeBarClipPath(
                      chromeStyle.studio.shape,
                      "studio",
                      `${2.1 * chromeStyle.studio.barScale}cqw`,
                    ),
                    WebkitClipPath: chromeBarClipPath(
                      chromeStyle.studio.shape,
                      "studio",
                      `${2.1 * chromeStyle.studio.barScale}cqw`,
                    ),
                    "&:active": { cursor: "grabbing" },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      fontWeight: 800,
                      fontSize: `${1.19 * chromeStyle.studio.fontScale}cqw`,
                      lineHeight: 1.2,
                      color: chromeStyle.studio.name,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {studioParts.studioName}
                  </Typography>
                  {studioParts.contactLine ? (
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 400,
                        fontSize: `${0.89 * chromeStyle.studio.fontScale}cqw`,
                        lineHeight: 1.35,
                        color: chromeStyle.studio.contact,
                        mt: "0.2cqw",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {studioParts.contactLine}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <MoodboardAddPhotosDialog
        open={addPhotosOpen}
        onClose={() => {
          if (!busy) {
            setAddPhotosOpen(false);
            setPoolPick(new Set());
          }
        }}
        busy={busy}
        pool={pool}
        poolLoading={poolLoading}
        poolError={poolError}
        usedSearchItemIds={usedSearchItemIds}
        pick={poolPick}
        setPick={setPoolPick}
        onConfirm={() => handleAddFromPool()}
        seasonLabel={board?.Temporada || ""}
        clientLabel={board?.CustomerName || board?.Titulo || ""}
      />

      <MoodboardBoardPhotosDialog
        open={boardPhotosOpen}
        onClose={() => {
          if (!busy) setBoardPhotosOpen(false);
        }}
        assets={assets}
        selectedIds={gallerySelected}
        usedOnPagesIds={usedOnPagesAssetIds}
        onToggleSelect={toggleGallerySelect}
        onClearSelect={() => setGallerySelected(new Set())}
        onPlaceOne={(id) => {
          if (activeSlotId) assignAssetToSlot(activeSlotId, id);
          else placeAssetInFirstEmpty(id);
        }}
        onPlaceSelected={() => {
          placeSelectedOnCurrentPage();
        }}
        onRemove={(id) => setAssetRemoveId(id)}
        pageLabel={`pág. ${pageIdx + 1}`}
        busy={busy}
      />

      <Dialog
        open={metaOpen}
        onClose={() => setMetaOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              color: "primary.main",
            }}
          >
            <TextFieldsOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {t("skillAgent.moodboard.pdfMeta")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.metaSubtitle")}
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label={t("skillAgent.form.title")}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.moodboard.topBar")}
              placeholder={t("skillAgent.moodboard.topBarPlaceholder")}
              value={categoryLabel}
              onChange={(e) => setCategoryLabel(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />
            <TextField
              size="small"
              label={t("skillAgent.moodboard.bottomBar")}
              value={studioLine}
              onChange={(e) => setStudioLine(e.target.value)}
              fullWidth
              sx={pageFieldSx}
            />

            <Divider sx={{ my: 0.5 }} />

            <Typography variant="subtitle2" fontWeight={800}>
              {t("skillAgent.moodboard.barStyleTitle")}
            </Typography>
            <ChromeShapePicker
              which="title"
              value={chromeStyle.title.shape}
              onChange={(shape) =>
                setChromeStyle((prev) => ({
                  ...prev,
                  title: { ...prev.title, shape },
                }))
              }
            />
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Tamaño barra {Math.round(chromeStyle.title.barScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.title.barScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    title: { ...prev.title, barScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                Tamaño letra {Math.round(chromeStyle.title.fontScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.title.fontScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    title: { ...prev.title, fontScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Fondo
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.title.bg}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        title: { ...prev.title, bg: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Texto
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.title.text}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        title: { ...prev.title, text: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Subtítulo
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.title.subText}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        title: { ...prev.title, subText: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Typography variant="subtitle2" fontWeight={800} sx={{ pt: 0.5 }}>
              Estilo barra pie
            </Typography>
            <ChromeShapePicker
              which="studio"
              value={chromeStyle.studio.shape}
              onChange={(shape) =>
                setChromeStyle((prev) => ({
                  ...prev,
                  studio: { ...prev.studio, shape },
                }))
              }
            />
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Tamaño barra {Math.round(chromeStyle.studio.barScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.studio.barScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    studio: { ...prev.studio, barScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                Tamaño letra {Math.round(chromeStyle.studio.fontScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={70}
                max={160}
                value={Math.round(chromeStyle.studio.fontScale * 100)}
                onChange={(_, v) =>
                  setChromeStyle((prev) => ({
                    ...prev,
                    studio: { ...prev.studio, fontScale: Number(v) / 100 },
                  }))
                }
                valueLabelDisplay="auto"
              />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Fondo
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.studio.bg}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        studio: { ...prev.studio, bg: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Nombre
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.studio.name}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        studio: { ...prev.studio, name: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" fontWeight={700}>
                    Contacto
                  </Typography>
                  <Box
                    component="input"
                    type="color"
                    value={chromeStyle.studio.contact}
                    onChange={(e) =>
                      setChromeStyle((prev) => ({
                        ...prev,
                        studio: { ...prev.studio, contact: e.target.value },
                      }))
                    }
                    sx={{
                      width: 36,
                      height: 28,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      bgcolor: "transparent",
                      cursor: "pointer",
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Button
              size="small"
              onClick={() => setChromeStyle(normalizeChromeStyle(null))}
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {t("skillAgent.moodboard.resetStyle")}
            </Button>
            <Button
              size="small"
              onClick={() =>
                setChromeStyle((prev) => ({
                  title: { ...prev.title, x: null, y: null },
                  studio: { ...prev.studio, x: null, y: null },
                }))
              }
              sx={{
                alignSelf: "flex-start",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              {t("skillAgent.moodboard.resetPosition")}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            variant="contained"
            onClick={() => setMetaOpen(false)}
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("skillAgent.common.done")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(slotRemoveId)}
        onClose={() => setSlotRemoveId(null)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {selectedSlotIds.size > 1 && selectedSlotIds.has(slotRemoveId)
            ? t("skillAgent.moodboard.dialog.removeSlotsTitle", {
                n: selectedSlotIds.size,
              })
            : t("skillAgent.moodboard.dialog.removeSlotTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {selectedSlotIds.size > 1 && selectedSlotIds.has(slotRemoveId)
              ? t("skillAgent.moodboard.dialog.removeSlotsBody")
              : t("skillAgent.moodboard.dialog.removeSlotBodyLong")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSlotRemoveId(null)}>
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleRemoveSlot(slotRemoveId)}
          >
            {t("skillAgent.common.remove")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assetRemoveId)}
        onClose={() => !busy && setAssetRemoveId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2.5,
              flexShrink: 0,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#f3f3f3",
              boxShadow: `0 8px 20px ${alpha("#0f172a", 0.12)}`,
            }}
          >
            {assetRemoveId ? (
              <AssetThumb
                assetId={assetRemoveId}
                selected={false}
                transparentBg
                fit="contain"
              />
            ) : null}
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: (t) => alpha(t.palette.error.main, 0.12),
                  color: "error.main",
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                {t("skillAgent.moodboard.dialog.removeAssetTitle")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.removeAssetBodyExtended")}
              {(() => {
                const used = pages.reduce(
                  (n, p) =>
                    n +
                    (p.slots || []).filter((s) => s.assetId === assetRemoveId)
                      .length,
                  0,
                );
                if (!used) return null;
                return (
                  <>
                    {" "}
                    {t("skillAgent.moodboard.dialog.removeAssetInSlots", {
                      n: used,
                    })}
                  </>
                );
              })()}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAssetRemoveId(null)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => handleRemoveAsset(assetRemoveId)}
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            {t("skillAgent.moodboard.dialog.removeAssetSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeBgConfirmOpen}
        onClose={() => !busy && setRemoveBgConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
              color: "warning.dark",
            }}
          >
            <AutoFixHighOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {t("skillAgent.moodboard.dialog.cutoutTitle")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              {t("skillAgent.moodboard.dialog.cutoutBodyLong")}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setRemoveBgConfirmOpen(false)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy}
            onClick={runRemoveBackground}
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoFixHighOutlinedIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            {t("skillAgent.moodboard.dialog.cutoutSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aviatorConfirmOpen}
        onClose={() => setAviatorConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 56,
              borderRadius: 2,
              flexShrink: 0,
              p: 0.6,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              border: "1px solid",
              borderColor: (t) => alpha(t.palette.primary.main, 0.22),
              display: "grid",
              gridTemplateColumns: "1.15fr 1fr 1fr 1fr",
              gridTemplateRows: "1fr 1fr",
              gap: 0.4,
            }}
            aria-hidden
          >
            <Box
              sx={{
                gridRow: "1 / 3",
                borderRadius: 0.75,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.45),
              }}
            />
            {Array.from({ length: 6 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  borderRadius: 0.5,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.28),
                }}
              />
            ))}
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.15, flex: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              <ViewQuiltOutlinedIcon color="primary" fontSize="small" />
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.25 }}
              >
                {t("skillAgent.moodboard.dialog.aviatorTitle")}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("skillAgent.moodboard.dialog.aviatorBody", {
                n: pageIdx + 1,
              })}
              {(() => {
                const n = (currentPage.slots || []).filter(
                  (s) => s.assetId,
                ).length;
                if (n <= 7) {
                  return n
                    ? ` ${t("skillAgent.moodboard.dialog.aviatorPageCount", {
                        n,
                      })}`
                    : null;
                }
                return ` ${t("skillAgent.moodboard.dialog.aviatorOverflow", {
                  n,
                })}`;
              })()}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAviatorConfirmOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAviatorPreset}
            startIcon={<ViewQuiltOutlinedIcon />}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            {t("skillAgent.moodboard.dialog.aviatorSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={autoPagesConfirmOpen}
        onClose={() => !busy && setAutoPagesConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.warning.main, 0.14),
              color: "warning.dark",
            }}
          >
            <GridViewOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {t("skillAgent.moodboard.dialog.autoPagesTitle")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              {t("skillAgent.moodboard.dialog.autoPagesBodyDetail", {
                n: assets.length,
              })}
              {pages.length > 1
                ? ` ${t("skillAgent.moodboard.dialog.autoPagesPagesNote", {
                    n: pages.length,
                  })}`
                : null}{" "}
              {t("skillAgent.moodboard.dialog.autoPagesNoDelete")}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setAutoPagesConfirmOpen(false)}
            disabled={busy}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={busy || !assets.length}
            onClick={handleAutoPages}
            startIcon={
              busy ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <GridViewOutlinedIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            {t("skillAgent.moodboard.dialog.autoPagesSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pageRemoveOpen}
        onClose={() => setPageRemoveOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (t) => alpha(t.palette.error.main, 0.12),
              color: "error.main",
            }}
          >
            <DeleteOutlineIcon />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.25 }}>
              {t("skillAgent.moodboard.dialog.deletePageTitle", {
                n: pageIdx + 1,
              })}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75 }}
            >
              {t("skillAgent.moodboard.dialog.deletePageBodyDetail")}
              {(currentPage.slots || []).filter((s) => s.assetId).length > 0
                ? ` ${t("skillAgent.moodboard.dialog.deletePagePhotosNow", {
                    n: (currentPage.slots || []).filter((s) => s.assetId)
                      .length,
                  })}`
                : null}
            </Typography>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setPageRemoveOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRemovePage}
            startIcon={<DeleteOutlineIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              boxShadow: "none",
            }}
          >
            {t("skillAgent.moodboard.dialog.deletePageSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(strokeAnchor)}
        anchorEl={strokeAnchor}
        onClose={() => setStrokeAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, width: 280, borderRadius: 2 } }}
      >
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
          {t("skillAgent.moodboard.dialog.strokeTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {t("skillAgent.moodboard.strokePopoverHint")}
        </Typography>
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t("skillAgent.moodboard.dialog.strokeWidth", {
                n: strokeWidth,
              })}
            </Typography>
            <Slider
              size="small"
              min={1}
              max={40}
              step={1}
              value={strokeWidth}
              onChange={(_, v) => setStrokeWidth(Number(v))}
            />
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {t("skillAgent.common.color")}
            </Typography>
            <Box
              component="input"
              type="color"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              sx={{
                width: 36,
                height: 28,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                p: 0,
                bgcolor: "transparent",
                cursor: "pointer",
              }}
            />
            <TextField
              size="small"
              value={strokeColor}
              onChange={(e) => setStrokeColor(e.target.value)}
              sx={{ flex: 1, "& input": { fontSize: 12, py: 0.75 } }}
            />
          </Stack>
          <Button
            variant="contained"
            size="small"
            disabled={busy || !activeSlot?.assetId}
            onClick={handleApplyContourStroke}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.moodboard.dialog.strokeApply")}
          </Button>
        </Stack>
      </Popover>

      <MoodboardCropDialog
        open={cropOpen}
        assetId={activeSlot?.assetId || null}
        busy={busy}
        onClose={() => setCropOpen(false)}
        onConfirm={handleConfirmCrop}
      />

      <Dialog
        open={exportConfirmOpen}
        onClose={() => !exporting && setExportConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (th) => alpha(th.palette.error.main, 0.1),
              border: "1px solid",
              borderColor: (th) => alpha(th.palette.error.main, 0.22),
              color: "error.main",
            }}
            aria-hidden
          >
            <PictureAsPdfOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ lineHeight: 1.25, mb: 0.75 }}
            >
              {t("skillAgent.moodboard.dialog.exportTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("skillAgent.moodboard.dialog.exportBody")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ px: 3, pb: 1 }}>
          <Stack
            spacing={1}
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.text.primary, 0.03),
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {[
              t("skillAgent.moodboard.dialog.exportBulletSave"),
              t("skillAgent.moodboard.dialog.exportBulletPdf"),
              t("skillAgent.moodboard.dialog.exportBulletDrive"),
            ].map((line) => (
              <Stack
                key={line}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "error.main",
                    mt: "7px",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {line}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.25, fontWeight: 600 }}
          >
            {t("skillAgent.moodboard.dialog.exportPagesNote", {
              n: (pages || []).length || 1,
              photos: (assets || []).length,
            })}
          </Typography>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setExportConfirmOpen(false)}
            disabled={exporting}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={exporting || busy || !assets.length}
            onClick={handleExport}
            startIcon={
              exporting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PictureAsPdfOutlinedIcon />
              )
            }
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            {exporting
              ? t("skillAgent.moodboard.exporting")
              : t("skillAgent.moodboard.dialog.exportSubmit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => !busy && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={800}>
          {t("skillAgent.moodboard.dialog.deleteBoardTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("skillAgent.moodboard.dialog.deleteBoardBody")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={busy}>
            {t("skillAgent.common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={handleDelete}
          >
            {t("skillAgent.common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
